<template>
  <!-- 데스크탑 플레이어 토글 버튼 (우측) -->
  <button
    v-if="globalMode && !isFullscreen"
    class="desktop-player-toggle"
    @click="isDesktopPlayerVisible = !isDesktopPlayerVisible"
    :class="{ 'active': isDesktopPlayerVisible }"
  >
    <span v-if="!isDesktopPlayerVisible">◀ {{ $t('player.player') }}</span>
    <span v-else>▶</span>
  </button>

  <!-- 모바일 플레이어 토글 버튼 -->
  <button
    v-if="globalMode && !isFullscreen"
    class="mobile-player-toggle"
    @click="isMobilePlayerVisible = !isMobilePlayerVisible"
    :class="{ 'active': isMobilePlayerVisible }"
  >
    <span v-if="!isMobilePlayerVisible">▲ {{ $t('player.openPlayer') }}</span>
    <span v-else>▼ {{ $t('player.closePlayer') }}</span>
  </button>

  <!-- 일반 플레이어 패널 -->
  <aside class="player-panel" :class="{
    'panel-fullscreen-mode': isFullscreen,
    'player-global': globalMode,
    'desktop-visible': isDesktopPlayerVisible,
    'mobile-visible': isMobilePlayerVisible
  }">
    <div class="player-header" v-show="!isFullscreen">
      <div class="player-title">Ttaechang Player</div>
      <div class="player-controls">
        <button v-if="playlistSongs.length > 0" class="clear-all-btn" @click="clearPlaylist" :title="$t('player.clearAll')">
          🗑️
        </button>
        <button class="expand-btn" @click="toggleFullscreen" :title="$t('player.expandFullscreen')">⤢</button>
      </div>
    </div>

    <div class="video-placeholder" v-if="!currentSong.youtubeId && !currentSong.videoId" v-show="!isFullscreen">
      <div>{{ $t('player.selectSong') }}</div>
    </div>

    <!-- YouTube Player - 일반 모드 -->
    <div v-show="!isFullscreen" class="video-container">
      <div id="youtube-player-normal" class="youtube-player"></div>
    </div>

    <!-- 떼창 예고 (작은 화면, 5-2초 전) -->
    <div class="subtitle-area" v-if="!isFullscreen && (currentSong.youtubeId || currentSong.videoId) && upcomingSection && isPlaying && !currentLyricLine">
      <div class="subtitle-upcoming">
        <div class="upcoming-text">
          🎤 {{ upcomingSection.name }} ({{ Math.ceil(upcomingSection.start - currentTime) }}{{ $t('player.upcomingSection') }})
        </div>
      </div>
    </div>

    <!-- 자막 영역 (영상 아래 별도 공간) - 타임스탬프 기반 실시간 자막 -->
    <div class="subtitle-area" v-if="!isFullscreen && (currentSong.youtubeId || currentSong.videoId) && currentLyricLine && isPlaying">
      <div class="realtime-subtitle-split">
        <!-- 원본 가사 (좌) -->
        <div class="subtitle-column original">
          <div class="subtitle-label">원본</div>
          <div class="subtitle-text">{{ currentLyricLine.text }}</div>
        </div>

        <!-- 발음/변환 가사 (우) -->
        <div class="subtitle-column translated" v-if="currentLyricLine.trans_text">
          <div class="subtitle-label">발음</div>
          <div class="subtitle-text">{{ currentLyricLine.trans_text }}</div>
        </div>
      </div>
    </div>

    <div class="song-info" v-show="!isFullscreen">
      <div class="current-song-title">{{ currentSong.title || $t('player.selectSong') }}</div>
      <div class="current-song-artist">{{ currentSong.artist || currentSong.channelTitle || $t('player.artist') }}</div>
    </div>

    <div class="player-controls" v-show="!isFullscreen">
      <button class="control-btn" @click="previousSong" :disabled="currentIndex <= 0">⏮</button>
      <button class="control-btn" @click="togglePlay">{{ isPlaying ? '⏸' : '▶' }}</button>
      <button class="control-btn" @click="nextSong">⏭</button>
      <button class="control-btn" @click="toggleRepeat" :class="{ active: isRepeating }">🔁</button>
    </div>

    <div class="progress-section" v-show="!isFullscreen">
      <div class="progress-header">
        <div class="progress-label">{{ $t('player.sectionManagement') }}</div>
      </div>
      <div class="progress-container">
        <div class="progress-wrapper">
          <!-- 전체 진행바 -->
          <input
            type="range"
            class="progress-bar"
            v-model="currentTime"
            :max="duration"
            @input="seekTo"
            @mousedown="handleTouchStart"
            @mouseup="handleTouchEnd"
            @touchstart="handleTouchStart"
            @touchend="handleTouchEnd"
          >
          <!-- YouTube UI 간섭 방지를 위해 터치 오버레이 제거 -->
          <!-- 구간 설정은 프로그레스 바 클릭으로만 가능 -->
          <!-- 선택된 구간 하이라이트 -->
          <div
            v-if="selectedSection.start !== null && selectedSection.end !== null"
            class="section-highlight"
            :style="getSectionHighlightStyle()"
          ></div>

          <!-- 저장된 구간 마커들 -->
          <div class="section-markers-container">
            <div
              v-for="section in currentSongSections"
              :key="section.id"
              class="section-marker"
              :class="{
                'active': currentActiveSection?.id === section.id,
                'upcoming': upcomingSection?.id === section.id
              }"
              :style="getSectionMarkerStyle(section)"
              :title="`${section.name} (${formatTime(section.start)} - ${formatTime(section.end)})`"
              @click="jumpToSection(section)"
            >
              <div class="section-marker-start"></div>
              <div class="section-marker-range"></div>
              <div class="section-marker-end"></div>
            </div>
          </div>
          <!-- 구간 설정 핸들 -->
          <div
            v-if="sectionMode && selectedSection.start !== null"
            class="section-handle start"
            :style="{ left: (selectedSection.start / duration * 100) + '%' }"
            draggable="false"
            @mousedown.stop.prevent="startDragHandle('start', $event)"
            @touchstart.stop.prevent="startDragHandle('start', $event)"
          ></div>
          <div
            v-if="sectionMode && selectedSection.end !== null"
            class="section-handle end"
            :style="{ left: (selectedSection.end / duration * 100) + '%' }"
            draggable="false"
            @mousedown.stop.prevent="startDragHandle('end', $event)"
            @touchstart.stop.prevent="startDragHandle('end', $event)"
          ></div>
        </div>
        <div class="time-info">
          <span class="time-display">{{ formatTime(currentTime) }} / {{ formatTime(duration) }}</span>
          <span v-if="selectedSection.start !== null && selectedSection.end !== null" class="section-info">
            {{ $t('player.sections') }}: {{ formatTime(selectedSection.start) }} ~ {{ formatTime(selectedSection.end) }}
          </span>
          <!-- 구간 선택 가이드 (한 줄 텍스트) -->
          <span v-if="sectionMode" class="guide-hint">
            💡 바를 길게 누르면 구간을 선택할 수 있어요
          </span>
        </div>

        <!-- 구간 설정 모드 토글 -->
        <div class="section-mode-controls">
          <button
            class="section-mode-btn"
            :class="{ active: sectionMode }"
            @click="toggleSectionMode"
            :title="$t('player.sectionSelect')"
          >
            {{ sectionMode ? $t('player.sectionSelecting') : `📐 ${$t('player.sectionSelect')}` }}
          </button>
          <button
            v-if="selectedSection.start !== null && selectedSection.end !== null"
            class="section-repeat-btn"
            :class="{ active: sectionRepeatMode }"
            @click="toggleSectionRepeat"
            :title="$t('player.sectionRepeat')"
          >
            🔁 {{ sectionRepeatMode ? $t('player.sectionRepeating') : $t('player.repeat') }}
          </button>
          <button
            v-if="selectedSection.start !== null && selectedSection.end !== null"
            class="section-save-btn"
            @click="openSectionSaveModal"
            :title="$t('player.saveSection')"
          >
            💾 {{ $t('player.save') }}
          </button>
          <button
            v-if="editingSectionId && selectedSection.start !== null && selectedSection.end !== null"
            class="section-delete-btn"
            @click="confirmDeleteSelectedSection"
            :title="$t('player.deleteSection')"
          >
            🗑️ {{ $t('common.delete') }}
          </button>
        </div>
      </div>

    </div>

    <!-- 플레이리스트 곡 목록 -->
    <div class="playlist-songs" v-show="!isFullscreen">
      <div class="playlist-header">
        <div class="playlist-title">{{ $t('player.playlist') }} ({{ playlistSongs.length }}{{ $t('player.songsCount') }})</div>
        <div class="playlist-controls" v-if="editable">
          <button
            class="control-btn small danger"
            @click="clearPlaylist"
            :title="$t('player.clearAll')"
          >
            {{ $t('player.clearAll') }}
          </button>
        </div>
      </div>

      <div class="songs-list">
        <div
          v-for="(song, index) in playlistSongs"
          :key="song.id || index"
          class="song-item"
          :class="{
            active: index === currentIndex,
            dragging: draggedSongIndex === index,
            'section-mode-active': sectionMode
          }"
          @click="playSongAt(index)"
          :draggable="!sectionMode"
          @dragstart="handleDragStart(index, $event)"
          @dragover.prevent="handleDragOver($event)"
          @drop="handleDrop(index, $event)"
          @dragend="handleDragEnd"
        >
          <div class="song-number">{{ index + 1 }}</div>
          <div class="song-details">
            <div class="song-title">{{ song.title }}</div>
            <div class="song-artist">{{ song.artist || song.channelTitle }}</div>
          </div>
          <div class="song-duration">{{ formatTime(song.duration || 0) }}</div>
          <button
            v-if="editable"
            class="remove-song-btn"
            @click.stop="removeSongFromPlayer(index)"
            :title="$t('player.removeFromPlaylist')"
          >
            ✕
          </button>
        </div>
      </div>
    </div>

    <!-- 카카오 애드핏 광고 (플레이어용 320x50) - 전체화면이 아닐 때만 -->
    <KakaoPlayerAd v-if="(isDesktopPlayerVisible || isMobilePlayerVisible) && !isFullscreen" />
  </aside>

  <!-- 전체화면 플레이어 -->
  <div class="fullscreen-player" :style="{ opacity: isFullscreen ? 1 : 0, pointerEvents: isFullscreen ? 'auto' : 'none', visibility: isFullscreen ? 'visible' : 'hidden' }">
    <div class="fullscreen-container">
      <!-- 헤더 -->
      <div class="fullscreen-header">
        <div class="fullscreen-title">Ttaechang Player</div>
        <div class="fullscreen-controls">
          <button v-if="playlistSongs.length > 0" class="clear-all-btn" @click="clearPlaylist" :title="$t('player.clearAll')">
            🗑️
          </button>
          <button class="close-btn" @click="toggleFullscreen" :title="$t('player.closeFullscreen')">✕</button>
        </div>
      </div>

      <!-- 메인 컨텐츠 -->
      <div class="fullscreen-main">
        <!-- 왼쪽: 플레이어 영역 -->
        <div class="fullscreen-player-area">
          <!-- YouTube Player - 전체화면 모드 -->
          <div v-show="isFullscreen" class="video-container fullscreen">
            <div id="youtube-player-fullscreen" class="youtube-player"></div>
          </div>

          <!-- 자막 영역 (전체화면, 영상 아래 별도 공간) - 자막이 실제로 있을 때만 표시 -->
          <!-- DEBUG: parsedLyrics.length={{ parsedLyrics.length }}, currentLyricLine={{ !!currentLyricLine }}, isPlaying={{ isPlaying }} -->
          <div class="subtitle-area fullscreen" v-if="currentSong.youtubeId || currentSong.videoId">
            <!-- 구간 선택 모드일 때 선택된 구간 정보 표시 (전체화면, 최우선) -->
            <div v-if="sectionMode && !isPlaying && selectedSection.start !== null && selectedSection.end !== null && (selectedSection.lyrics || selectedSection.name)" class="realtime-subtitle fullscreen section-mode">
              <div class="subtitle-header">
                📐 {{ $t('player.selectedSection') }}: {{ selectedSection.name || `${formatTime(selectedSection.start)} - ${formatTime(selectedSection.end)}` }}
              </div>
              <div v-if="selectedSection.lyrics" class="subtitle-content">
                {{ selectedSection.lyrics }}
              </div>
              <div v-if="selectedSection.pronunciation" class="subtitle-pronunciation">
                {{ selectedSection.pronunciation }}
              </div>
            </div>

            <!-- 실시간 자막 표시 (전체화면, 3줄 좌우 분할) -->
            <div v-else-if="currentLyricLine && isPlaying" class="realtime-subtitle-3lines fullscreen">
              <!-- 이전 줄 (작은 글씨) -->
              <div class="subtitle-line previous" v-if="previousLyricLine">
                <div class="subtitle-split">
                  <div class="subtitle-column">{{ previousLyricLine.text }}</div>
                  <div class="subtitle-column" v-if="previousLyricLine.trans_text">{{ previousLyricLine.trans_text }}</div>
                </div>
              </div>

              <!-- 현재 줄 (굵고 큰 글씨) -->
              <div class="subtitle-line current">
                <div class="subtitle-split">
                  <div class="subtitle-column">{{ currentLyricLine.text }}</div>
                  <div class="subtitle-column" v-if="currentLyricLine.trans_text">{{ currentLyricLine.trans_text }}</div>
                </div>
              </div>

              <!-- 다음 줄 (작은 글씨) -->
              <div class="subtitle-line next" v-if="nextLyricLine">
                <div class="subtitle-split">
                  <div class="subtitle-column">{{ nextLyricLine.text }}</div>
                  <div class="subtitle-column" v-if="nextLyricLine.trans_text">{{ nextLyricLine.trans_text }}</div>
                </div>
              </div>
            </div>

            <!-- 자막 없을 때 떼창 예고만 표시 (전체화면, 구간 선택 모드가 아닐 때만) -->
            <div v-else-if="!sectionMode && upcomingSection" class="subtitle-upcoming fullscreen">
              <div class="upcoming-text">
                🎤 {{ upcomingSection.name }} ({{ Math.ceil(upcomingSection.start - currentTime) }}{{ $t('player.upcomingSection') }})
              </div>
            </div>
          </div>

          <div class="song-info">
            <div class="current-song-title">{{ currentSong.title || $t('player.selectSong') }}</div>
            <div class="current-song-artist">{{ currentSong.artist || currentSong.channelTitle || $t('player.artist') }}</div>
          </div>

          <div class="player-controls">
            <button class="control-btn" @click="previousSong" :disabled="currentIndex <= 0">⏮</button>
            <button class="control-btn" @click="togglePlay">{{ isPlaying ? '⏸' : '▶' }}</button>
            <button class="control-btn" @click="nextSong">⏭</button>
            <button class="control-btn" @click="toggleRepeat" :class="{ active: isRepeating }">🔁</button>
          </div>

          <div class="progress-section">
            <div class="progress-header">
              <div class="progress-label">{{ $t('player.sectionManagement') }}</div>
            </div>
            <div class="progress-container">
              <div class="progress-wrapper">
                <!-- 전체 진행바 -->
                <input
                  type="range"
                  class="progress-bar"
                  v-model="currentTime"
                  :max="duration"
                  @input="seekTo"
                  @mousedown="handleTouchStart"
                  @mouseup="handleTouchEnd"
                  @touchstart="handleTouchStart"
                  @touchend="handleTouchEnd"
                >
                <!-- YouTube UI 간섭 방지를 위해 터치 오버레이 제거 -->
                <!-- 구간 설정은 프로그레스 바 클릭으로만 가능 -->
                <!-- 선택된 구간 하이라이트 -->
                <div
                  v-if="selectedSection.start !== null && selectedSection.end !== null"
                  class="section-highlight"
                  :style="getSectionHighlightStyle()"
                ></div>

                <!-- 저장된 구간 마커들 -->
                <div class="section-markers-container">
                  <div
                    v-for="section in currentSongSections"
                    :key="section.id"
                    class="section-marker fullscreen"
                    :class="{
                      'active': currentActiveSection?.id === section.id,
                      'upcoming': upcomingSection?.id === section.id
                    }"
                    :style="getSectionMarkerStyle(section)"
                    :title="`${section.name} (${formatTime(section.start)} - ${formatTime(section.end)})`"
                    @click="jumpToSection(section)"
                  >
                    <div class="section-marker-start"></div>
                    <div class="section-marker-range"></div>
                    <div class="section-marker-end"></div>
                  </div>
                </div>
                <!-- 구간 설정 핸들 -->
                <div
                  v-if="sectionMode && selectedSection.start !== null"
                  class="section-handle start"
                  :style="{ left: (selectedSection.start / duration * 100) + '%' }"
                  draggable="false"
                  @mousedown.stop.prevent="startDragHandle('start', $event)"
                  @touchstart.stop.prevent="startDragHandle('start', $event)"
                ></div>
                <div
                  v-if="sectionMode && selectedSection.end !== null"
                  class="section-handle end"
                  :style="{ left: (selectedSection.end / duration * 100) + '%' }"
                  draggable="false"
                  @mousedown.stop.prevent="startDragHandle('end', $event)"
                  @touchstart.stop.prevent="startDragHandle('end', $event)"
                ></div>
              </div>
              <div class="time-info">
                <span class="time-display">{{ formatTime(currentTime) }} / {{ formatTime(duration) }}</span>
                <span v-if="selectedSection.start !== null && selectedSection.end !== null" class="section-info">
                  {{ $t('player.sectionTime') }} {{ formatTime(selectedSection.start) }} ~ {{ formatTime(selectedSection.end) }}
                </span>
                <!-- 구간 선택 가이드 (전체화면) -->
                <span v-if="sectionMode" class="guide-hint">
                  💡 바를 길게 누르면 구간을 선택할 수 있어요
                </span>
              </div>

              <!-- 구간 설정 모드 토글 (전체화면) -->
              <div class="section-mode-controls fullscreen">
                <button
                  class="section-mode-btn"
                  :class="{ active: sectionMode }"
                  @click="toggleSectionMode"
                  :title="$t('player.sectionSelect')"
                >
                  {{ sectionMode ? $t('player.sectionSelecting') : `📐 ${$t('player.sectionSelect')}` }}
                </button>
                <button
                  v-if="selectedSection.start !== null && selectedSection.end !== null"
                  class="section-repeat-btn"
                  :class="{ active: sectionRepeatMode }"
                  @click="toggleSectionRepeat"
                  :title="$t('player.sectionRepeat')"
                >
                  🔁 {{ sectionRepeatMode ? $t('player.sectionRepeating') : $t('player.repeatMode') }}
                </button>
                <button
                  v-if="selectedSection.start !== null && selectedSection.end !== null"
                  class="section-save-btn"
                  @click="openSectionSaveModal"
                  :title="$t('player.saveSection')"
                >
                  💾 {{ $t('player.saveBtn') }}
                </button>
                <button
                  v-if="editingSectionId && selectedSection.start !== null && selectedSection.end !== null"
                  class="section-delete-btn"
                  @click="confirmDeleteSelectedSection"
                  :title="$t('player.deleteSection')"
                >
                  🗑️ {{ $t('player.deleteBtn') }}
                </button>
              </div>
            </div>





          </div>

        </div>

        <!-- 오른쪽: 재생목록 영역 -->
        <div class="fullscreen-playlist-area">
          <div class="playlist-header">
            <div class="playlist-title">{{ $t('player.playlist') }} ({{ playlistSongs.length }}{{ $t('player.songsCount') }})</div>
            <div class="playlist-controls" v-if="editable">
              <button
                class="control-btn small danger"
                @click="clearPlaylist"
                :title="$t('player.clearAll')"
              >
                {{ $t('player.clearAllPlaylist') }}
              </button>
            </div>
          </div>

          <div class="songs-list">
            <div
              v-for="(song, index) in playlistSongs"
              :key="song.id || index"
              class="song-item"
              :class="{
                active: index === currentIndex,
                dragging: draggedSongIndex === index,
                'section-mode-active': sectionMode
              }"
              @click="playSongAt(index)"
              :draggable="!sectionMode"
              @dragstart="handleDragStart(index, $event)"
              @dragover.prevent="handleDragOver($event)"
              @drop="handleDrop(index, $event)"
              @dragend="handleDragEnd"
            >
              <div class="song-number">{{ index + 1 }}</div>
              <div class="song-details">
                <div class="song-title">{{ song.title }}</div>
                <div class="song-artist">{{ song.artist || song.channelTitle }}</div>
              </div>
              <div class="song-duration">{{ formatTime(song.duration || 0) }}</div>
              <button
                v-if="editable"
                class="remove-song-btn"
                @click.stop="removeSongFromPlayer(index)"
                :title="$t('player.removeFromPlaylist')"
              >
                ✕
              </button>
            </div>
          </div>

          <!-- 전체화면 플레이리스트 하단 광고 -->
          <KakaoPlayerAd v-if="isFullscreen" :key="'fullscreen-ad'" :delay="800" />
        </div>
      </div>
    </div>
  </div>

  <!-- 구간 저장 모달 -->
  <div v-if="showSectionSaveModal" class="modal-overlay">
    <div class="modal-container" @click.stop>
      <div class="modal-header">
        <h3>{{ $t('player.sectionSaveModal') }}</h3>
        <button class="modal-close-btn" @click="closeSectionSaveModal">✕</button>
      </div>

      <div class="modal-body">

        <div class="form-group">
          <label for="section-name">{{ $t('player.sectionName') }} <span class="required">{{ $t('player.required') }}</span></label>
          <input
            id="section-name"
            v-model="sectionSaveForm.name"
            type="text"
            :placeholder="$t('player.sectionNamePlaceholder')"
            required
          />
        </div>

        <!-- AI 가사 추출 영역 -->
        <div class="form-group">
          <div class="lyrics-extract-header">
            <label>{{ $t('player.sectionLyrics') }}</label>
            <div class="ai-extract-controls">
              <!-- 언어 선택 -->
              <div class="language-selector-inline">
                <label>출력 언어:</label>
                <select v-model="lyricsOutputLanguage">
                  <option value="ko">한글 (KOR)</option>
                  <option value="en">영어 (ENG)</option>
                  <option value="ja">일어 (JPN)</option>
                </select>
              </div>

              <!-- AI 추출 버튼 -->
              <button
                v-if="!showLyricsExtractor"
                type="button"
                class="ai-extract-btn"
                :class="{ 'loading': isCheckingWhisperServer }"
                :disabled="isCheckingWhisperServer"
                @click="toggleLyricsExtractor"
              >
                <span v-if="isCheckingWhisperServer" class="spinner-small"></span>
                <span v-else>🎤</span>
                {{ isCheckingWhisperServer ? '서버 확인 중...' : 'AI 가사 자동 추출' }}
              </button>
              <button
                v-else
                type="button"
                class="ai-extract-btn secondary"
                @click="toggleLyricsExtractor"
              >
                ✕ 추출 취소
              </button>
            </div>
          </div>

          <!-- LyricsExtractor 컴포넌트 -->
          <div v-if="showLyricsExtractor" class="lyrics-extractor-container">
            <LyricsExtractor
              v-if="currentSong.youtubeId || currentSong.videoId"
              :video-id="currentSong.youtubeId || currentSong.videoId"
              :start-time="Math.floor(selectedSection.start)"
              :end-time="Math.ceil(selectedSection.end)"
              :output-language="lyricsOutputLanguage"
              :auto-start="true"
              @lyrics-extracted="onLyricsExtracted"
              @extraction-error="onExtractionError"
            />
          </div>

          <!-- 가사 입력 필드 (좌우 배치) -->
          <div class="lyrics-input-row">
            <div class="lyrics-input-column">
              <label for="section-lyrics">원본 가사</label>
              <textarea
                id="section-lyrics"
                v-model="sectionSaveForm.lyrics"
                :placeholder="$t('player.sectionLyricsPlaceholder')"
                rows="5"
              ></textarea>
            </div>
            <div class="lyrics-input-column">
              <label for="section-pronunciation">{{ $t('player.pronunciation') }}</label>
              <textarea
                id="section-pronunciation"
                v-model="sectionSaveForm.pronunciation"
                :placeholder="$t('player.pronunciationPlaceholder')"
                rows="5"
              ></textarea>
            </div>
          </div>
        </div>
      </div>

      <div class="modal-footer">
        <button class="modal-btn secondary" @click="closeSectionSaveModal">
          {{ $t('player.cancel') }}
        </button>
        <button
          class="modal-btn primary"
          @click="saveSection"
          :disabled="!sectionSaveForm.name.trim()"
        >
          💾 {{ $t('player.save') }}
        </button>
      </div>
    </div>
  </div>

  <!-- 가사 저장 모달 -->
  <div v-if="showLyricsSaveModal" class="modal-overlay">
    <div class="modal-container" @click.stop>
      <div class="modal-header">
        <h3>{{ editingLyricId ? $t('player.lyricsEditModal') : $t('player.lyricsSaveModal') }}</h3>
        <button class="modal-close-btn" @click="closeLyricsSaveModal">✕</button>
      </div>

      <div class="modal-body">
        <div class="form-group">
          <label for="lyrics-name">{{ $t('player.sectionName') }} <span class="required">{{ $t('player.required') }}</span></label>
          <input
            id="lyrics-name"
            v-model="lyricsSaveForm.name"
            type="text"
            :placeholder="$t('player.sectionNamePlaceholder')"
            required
          />
        </div>

        <div class="form-group">
          <label for="lyrics-text">{{ $t('player.lyricsText') }} <span class="required">{{ $t('player.required') }}</span></label>
          <textarea
            id="lyrics-text"
            v-model="lyricsSaveForm.lyrics"
            :placeholder="$t('player.lyricsTextPlaceholder')"
            rows="4"
            required
          ></textarea>
        </div>

        <div class="form-group">
          <label for="lyrics-pronunciation">{{ $t('player.pronunciationGuide') }}</label>
          <textarea
            id="lyrics-pronunciation"
            v-model="lyricsSaveForm.pronunciation"
            :placeholder="$t('player.pronunciationGuidePlaceholder')"
            rows="2"
          ></textarea>
        </div>

        <div class="form-group">
          <label for="lyrics-line">{{ $t('player.lineNumber') }}</label>
          <input
            id="lyrics-line"
            v-model="lyricsSaveForm.lineNumber"
            type="number"
            min="1"
            :placeholder="$t('player.lineNumberPlaceholder')"
          />
        </div>

        <div class="form-group">
          <label class="checkbox-label">
            <input
              type="checkbox"
              v-model="lyricsSaveForm.isPublic"
            />
            {{ $t('player.shareWithOthers') }}
          </label>
        </div>
      </div>

      <div class="modal-footer">
        <button class="modal-btn secondary" @click="closeLyricsSaveModal">
          {{ $t('player.cancel') }}
        </button>
        <button
          class="modal-btn primary"
          @click="saveLyricsSection"
          :disabled="!lyricsSaveForm.name.trim() || !lyricsSaveForm.lyrics.trim()"
        >
          🎤 {{ editingLyricId ? $t('common.update') : $t('player.save') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth'
import { userPlaylistService } from '@/services/userPlaylistService'
import { songService } from '@/services/songService'
import { sectionService } from '@/services/sectionService'
import { getWhisperHealth } from '@/services/lyricsService'
import { customAlert } from '@/utils/customAlert'
import LyricsExtractor from '@/components/LyricsExtractor.vue'
import KakaoPlayerAd from '@/components/KakaoPlayerAd.vue'

const { t } = useI18n()

// Props
const props = defineProps({
  songs: {
    type: Array,
    default: () => []
  },
  playlist: {
    type: Object,
    default: null
  },
  editable: {
    type: Boolean,
    default: true
  },
  globalMode: {
    type: Boolean,
    default: false
  }
})

// Emits
const emit = defineEmits(['songRemoved', 'playlistApplied', 'fullscreen-change'])

// Reactive data
const playerId = ref(`qcass${Math.random().toString(36).substr(2, 9)}`)
const youtubePlayerElement = ref(null)
const currentSong = ref({})
const currentIndex = ref(0)
const playlistSongs = ref([])
const isPlaying = ref(false)
const isRepeating = ref(false)
const currentTime = ref(0)
const duration = ref(0)
const playerState = ref(-1) // YouTube Player 상태 (-1: 시작 안 됨, 0: 종료됨, 1: 재생 중, 2: 일시정지, 3: 버퍼링, 5: 비디오 큐됨)
const youtubePlayerNormal = ref(null)
const youtubePlayerFullscreen = ref(null)
const youtubePlayer = computed(() => isFullscreen.value ? youtubePlayerFullscreen.value : youtubePlayerNormal.value)
const playerReady = ref(false)
const isFullscreen = ref(false)
const isMobilePlayerVisible = ref(false) // 모바일에서 플레이어 표시 여부
const isDesktopPlayerVisible = ref(false) // 데스크탑에서 플레이어 표시 여부

// 구간 반복 시스템
const sectionMode = ref(false) // 구간 설정 모드
const selectedSection = ref({
  start: null,
  end: null,
  name: ''
})
const sectionRepeatMode = ref(false) // 구간 반복 재생 모드
const sections = ref([]) // 전체 구간들 (savedSections와 동일, 호환성 유지)
const savedSections = ref([]) // 저장된 구간들
const showSectionSaveModal = ref(false) // 구간 저장 모달 표시
const showLyricsSaveModal = ref(false) // 가사 저장 모달 표시
const sectionSaveForm = ref({
  name: '',
  lyrics: '',
  pronunciation: ''
}) // 구간 저장 폼 데이터
const showLyricsExtractor = ref(false) // AI 가사 추출 UI 표시 여부
const lyricsOutputLanguage = ref('ko') // 가사 출력 언어 (ko/en/ja)
const whisperServerAvailable = ref(null) // Whisper 서버 상태 (null: 미확인, true: 가능, false: 불가)
const isCheckingWhisperServer = ref(false) // Whisper 서버 상태 확인 중
const isDraggingHandle = ref(false)
const dragHandleType = ref(null) // 'start' or 'end'

// 선택된 저장 구간
const selectedSavedSection = ref(null)
const isSavedSectionRepeating = ref(false)
const editingSectionId = ref(null)

// 현재 활성화된 구간 (재생 중인 구간)
const currentActiveSection = ref(null)
const upcomingSection = ref(null) // 곧 시작될 구간 (3-5초 전)

// 실시간 자막 (타임스탬프 기반)
const currentLyricLine = ref(null) // 현재 자막 라인 { time, text, trans_text }
const parsedLyrics = ref([]) // 파싱된 가사 배열 [{ time, text, trans_text }]
const currentLyricIndex = ref(-1) // 현재 자막 라인의 인덱스

// 이전 자막 라인 (computed)
const previousLyricLine = computed(() => {
  if (currentLyricIndex.value > 0 && parsedLyrics.value.length > 0) {
    return parsedLyrics.value[currentLyricIndex.value - 1]
  }
  return null
})

// 다음 자막 라인 (computed)
const nextLyricLine = computed(() => {
  if (currentLyricIndex.value >= 0 && currentLyricIndex.value < parsedLyrics.value.length - 1) {
    return parsedLyrics.value[currentLyricIndex.value + 1]
  }
  return null
})


// 재생 횟수 추적 (30초 재생 시 카운트)
const playCountTimer = ref(null)
const playCountCounted = ref(new Set()) // 이미 카운트된 곡들의 ID 저장
const accumulatedPlayTime = ref(0) // 누적 재생 시간 (초)
const lastPlayTime = ref(0) // 마지막 재생 시점
const lastSongKey = ref('') // 마지막 곡 식별키

// Computed properties
// 현재 곡의 저장된 구간만 표시
const currentSongSections = computed(() => {
  if (!currentSong.value.youtubeId && !currentSong.value.videoId && !currentSong.value.id) {
    return []
  }

  // currentSong에 sections가 있으면 우선 사용 (API에서 받은 구간)
  if (currentSong.value.sections && currentSong.value.sections.length > 0) {
    return currentSong.value.sections
  }

  // 없으면 savedSections에서 찾기 (기존 로직)
  const currentYouTubeId = currentSong.value.youtubeId || currentSong.value.videoId
  const currentDbId = currentSong.value.id

  const filteredSections = savedSections.value.filter(section => {
    let matches = false

    // YouTube ID로 매칭 (로컬/서버 구간 모두)
    if (currentYouTubeId) {
      matches = section.songId === currentYouTubeId ||
               section.youtubeId === currentYouTubeId ||
               section.videoId === currentYouTubeId ||
               section.songTitle === currentSong.value.title
    }

    // YouTube ID가 없는 경우 제목으로 매칭
    if (!matches && !currentYouTubeId && currentSong.value.title) {
      matches = section.songTitle === currentSong.value.title
    }

    // 데이터베이스 ID로 매칭 (서버 저장 섹션)
    if (!matches && currentDbId) {
      if (section.dbId && section.dbId === currentDbId) {
        matches = true
      } else if (section.songId) {
        const dbIdMatch = section.songId.match(/^db_(\d+)$/)
        if (dbIdMatch) {
          matches = parseInt(dbIdMatch[1]) === currentDbId
        }
      }
    }

    return matches
  })

  return filteredSections
})

// 현재 곡이 데이터베이스에 있는지 확인 (구간 저장 가능 여부)
const canSaveSection = computed(() => {
  return currentSong.value.id && currentSong.value.id > 0 // Songs 테이블의 실제 ID가 있는지 확인
})

// 현재 곡의 가사 구간들 (시간순 정렬, 중복 제거)
const currentSongLyrics = computed(() => {
  // 현재 곡의 저장된 구간들의 serverId 목록
  const existingServerIds = new Set(
    currentSongSections.value
      .filter(s => s.serverId)
      .map(s => s.serverId)
  )

  // 이미 구간반복 리스트에 있는 것들은 제외하고 가사만 표시
  const filteredLyrics = currentLyrics.value.filter(lyric => {
    return !existingServerIds.has(lyric.id)
  })

  return filteredLyrics.sort((a, b) => {
    // lineNumber가 있으면 우선 정렬, 없으면 시간순
    if (a.lineNumber && b.lineNumber) {
      return a.lineNumber - b.lineNumber
    }
    return a.startTime - b.startTime
  })
})

// 드래그앤드롭
const draggedSongIndex = ref(null)

// Auth Store
const authStore = useAuthStore()
const router = useRouter()

// 페이지 이동 시 재생 상태 저장
const savePlaybackState = () => {
  try {
    if (currentSong.value && youtubePlayer.value && playerReady.value) {
      const playbackState = {
        currentSong: currentSong.value,
        currentIndex: currentIndex.value,
        playlistSongs: playlistSongs.value,
        currentTime: youtubePlayer.value.getCurrentTime ? youtubePlayer.value.getCurrentTime() : currentTime.value,
        isPlaying: isPlaying.value,
        timestamp: Date.now()
      }

      localStorage.setItem('player_playback_state', JSON.stringify(playbackState))
      sessionStorage.setItem('player_playback_state', JSON.stringify(playbackState))
    }
  } catch (error) {
    console.error('재생 상태 저장 실패:', error)
  }
}

// 페이지 로드 시 재생 상태 복원
const restorePlaybackState = async () => {
  try {
    // 로그아웃 상태면 상태 복원하지 않고 저장된 데이터 완전 삭제
    if (!authStore.isLoggedIn) {
      console.log('🚫 로그아웃 상태 - 플레이어 상태 복원 건너뜀')
      // localStorage 플레이어 데이터 완전 삭제
      localStorage.removeItem('player_playback_state')
      localStorage.removeItem('ttaechang_saved_sections')
      localStorage.removeItem('ttaechang_current_playlist')
      sessionStorage.removeItem('player_playback_state')
      console.log('🗑️ restorePlaybackState - localStorage 플레이어 데이터 완전 삭제')
      return
    }

    let savedState = localStorage.getItem('player_playback_state')
    if (!savedState) {
      savedState = sessionStorage.getItem('player_playback_state')
    }
    if (!savedState) return

    const state = JSON.parse(savedState)

    // 5분 이상 지난 상태는 복원하지 않음
    if (Date.now() - state.timestamp > 5 * 60 * 1000) {
      localStorage.removeItem('player_playback_state')
      return
    }

    // 재생목록 및 재생 상태 복원
    playlistSongs.value = state.playlistSongs || []
    currentIndex.value = state.currentIndex || 0
    console.log('🔄 상태 복원 - currentSong 설정:', state.currentSong)

    if (state.currentSong && Object.keys(state.currentSong).length > 0) {
      // 상태 복원 시에도 데이터베이스 ID 포함하여 설정
      currentSong.value = {
        id: state.currentSong.id, // Songs 테이블 ID
        youtubeId: state.currentSong.youtubeId || state.currentSong.videoId,
        videoId: state.currentSong.youtubeId || state.currentSong.videoId,
        title: state.currentSong.title,
        artist: state.currentSong.artist,
        thumbnailUrl: state.currentSong.thumbnailUrl,
        duration: state.currentSong.duration
      }
    } else {
      currentSong.value = {}
    }

    console.log('🔄 상태 복원 후 currentSong:', currentSong.value)

    if (currentSong.value && (currentSong.value.youtubeId || currentSong.value.videoId)) {
      await nextTick()
      await initYouTubePlayer()

      // 재생 위치 복원 (재생 상태는 복원하지 않음 - 사용자가 직접 재생 버튼 클릭 필요)
      setTimeout(() => {
        if (youtubePlayer.value && playerReady.value) {
          try {
            if (state.currentTime > 0 && typeof youtubePlayer.value.seekTo === 'function') {
              youtubePlayer.value.seekTo(state.currentTime, true)
              currentTime.value = state.currentTime
            }

            // 새로고침 시 자동 재생하지 않음 (브라우저 정책상 사용자 인터랙션 필요)
            if (typeof youtubePlayer.value.pauseVideo === 'function') {
              youtubePlayer.value.pauseVideo()
            }
            isPlaying.value = false
            console.log('⏸ 재생 상태 복원: 일시정지 (사용자가 직접 재생 버튼을 눌러야 함)')
          } catch (playerError) {
            console.error('YouTube 플레이어 메서드 호출 에러:', playerError)
          }
        }
      }, 1000)
    }
  } catch (error) {
    console.error('재생 상태 복원 실패:', error)
    localStorage.removeItem('player_playback_state')
  }
}

// 라우터 변경 감지 (globalMode가 아닐 때만)
watch(() => router.currentRoute.value.path, async (newPath, oldPath) => {
  console.log('라우터 변경:', oldPath, '→', newPath)

  // globalMode일 때는 페이지 이동해도 플레이어를 유지
  if (props.globalMode) {
    console.log('🌍 글로벌 모드 - 재생 계속 유지')
    return
  }

  // 재생 상태 저장
  savePlaybackState()

  // 페이지 이동 후 YouTube 플레이어 재초기화 필요 (globalMode가 아닐 때만)
  if (newPath !== oldPath && currentSong.value && (currentSong.value.youtubeId || currentSong.value.videoId)) {
    console.log('🎵 페이지 이동 후 YouTube 플레이어 재초기화')

    // 잠시 후 플레이어 재초기화
    setTimeout(async () => {
      try {
        await initYouTubePlayer()
        console.log('🎵 YouTube 플레이어 재초기화 완료')
      } catch (error) {
        console.error('YouTube 플레이어 재초기화 실패:', error)
      }
    }, 500)
  }
})

// 로그인/로그아웃 감지 제거 - 직접 호출로만 처리

// 실시간 감지 모두 제거

// 라우터 네비게이션 가드 - 재생 상태만 저장
router.beforeEach((to, from, next) => {
  savePlaybackState()
  next()
})

// 페이지 언로드 시 상태 저장
const handleBeforeUnload = () => {
  savePlaybackState()
}

// 패널 초기 높이 저장
let initialPanelHeight = null

// 패널 초기 높이 계산 (최초 1회만)
const initPlayerPanelHeight = (retryCount = 0) => {
  if (!props.globalMode) return

  const playerPanel = document.querySelector('.player-panel.player-global')
  const header = document.querySelector('.header')

  if (!playerPanel || !header) {
    console.warn('플레이어 패널 또는 헤더를 찾지 못함', {
      playerPanel: !!playerPanel,
      header: !!header,
      retryCount
    })

    // 최대 5번까지 재시도 (500ms 간격)
    if (retryCount < 5) {
      setTimeout(() => initPlayerPanelHeight(retryCount + 1), 500)
    }
    return
  }

  // 모바일에서는 CSS로 처리하므로 데스크탑만 적용
  if (window.innerWidth <= 1024) return

  const headerRect = header.getBoundingClientRect()
  const headerHeight = headerRect.height
  const viewportHeight = window.innerHeight

  // position: fixed이므로 스크롤과 관계없이 고정된 값 사용
  // 헤더 높이만큼 아래에 위치
  const topPosition = headerHeight

  // 높이 = 브라우저 높이 - 헤더높이 * 2
  const panelHeight = viewportHeight - (headerHeight * 2)

  initialPanelHeight = panelHeight
  playerPanel.style.height = `${panelHeight}px`
  playerPanel.style.top = `${topPosition}px`

  console.log('✅ 패널 초기 높이 설정 성공 (fixed position):', {
    height: panelHeight,
    top: topPosition,
    headerHeight: headerHeight,
    viewportHeight: viewportHeight,
    retryCount: retryCount
  })
}


// YouTube Player 초기화
const initYouTubePlayer = async () => {
  if (!currentSong.value || (!currentSong.value.youtubeId && !currentSong.value.videoId)) {
    return
  }

  const videoId = currentSong.value.youtubeId || currentSong.value.videoId

  try {
    if (!window.YT || !window.YT.Player) {
      // YouTube API가 index.html에서 로드되므로 대기
      await new Promise((resolve, reject) => {
        if (window.YT && window.YT.Player) {
          resolve()
          return
        }

        // YouTube API 준비 콜백
        window.onYouTubeIframeAPIReady = () => {
          console.log('YouTube API 준비 완료')
          resolve()
        }

        // 최대 5초 대기
        setTimeout(() => {
          if (window.YT && window.YT.Player) {
            console.log('YouTube API 준비됨 (타임아웃)')
            resolve()
          } else {
            console.error('YouTube API 로드 타임아웃')
            reject(new Error('YouTube API 로드 실패'))
          }
        }, 5000)
      })
    }

    initPlayerInstance(videoId)
  } catch (error) {
    console.error('YouTube 플레이어 초기화 실패:', error)
  }
}

const initPlayerInstance = (videoId) => {
  // 일반 모드 플레이어 초기화
  initSinglePlayer('youtube-player-normal', videoId, youtubePlayerNormal, '225')
  // 전체화면 모드 플레이어 초기화
  initSinglePlayer('youtube-player-fullscreen', videoId, youtubePlayerFullscreen, '800')
}

const initSinglePlayer = (playerElementId, videoId, playerRef, playerHeight) => {
  const playerElement = document.getElementById(playerElementId)

  if (!playerElement) {
    console.error('YouTube 플레이어 DOM 요소를 찾을 수 없음:', playerElementId)
    return
  }

  console.log('YouTube 플레이어 DOM 요소 찾음:', playerElementId)

  if (playerRef.value) {
    try {
      // 먼저 iframe 존재 여부 확인
      const iframe = playerRef.value.getIframe?.()
      if (!iframe) {
        console.warn('⚠️ 플레이어 객체는 있지만 iframe이 없습니다. 재생성합니다:', playerElementId)
        throw new Error('iframe not found - need to recreate player')
      }

      // 기존 플레이어의 상태 확인
      if (typeof playerRef.value.getVideoData === 'function') {
        const currentVideoData = playerRef.value.getVideoData()
        if (currentVideoData.video_id !== videoId) {
          console.log('🎵 새로운 비디오 큐 (재생 안 함):', videoId, playerElementId)
          if (typeof playerRef.value.cueVideoById === 'function') {
            // cueVideoById: 비디오 로드만 하고 재생하지 않음 (playSongAt에서 명시적으로 재생)
            playerRef.value.cueVideoById(videoId)

            // 기존 플레이어에 새 비디오 큐잉했으므로 준비 상태 유지
            if (playerElementId.includes('normal')) {
              playerReady.value = true
            }
          } else {
            throw new Error('cueVideoById method not available')
          }
        } else {
          console.log('🎵 동일한 비디오 - iframe 존재 확인:', playerElementId, iframe ? '✅' : '❌')
        }
      } else {
        // getVideoData가 없으면 강제 로드
        if (playerRef.value && typeof playerRef.value.cueVideoById === 'function') {
          playerRef.value.cueVideoById(videoId)
          if (playerElementId.includes('normal')) {
            playerReady.value = true
          }
        } else {
          console.warn('YouTube 플레이어의 cueVideoById 메서드를 사용할 수 없음')
          throw new Error('Invalid YouTube player instance')
        }
      }
      return
    } catch (error) {
      console.error('기존 플레이어 로드 실패:', error)
      // 실패하면 플레이어를 완전히 재생성
      try {
        playerRef.value.destroy()
      } catch (destroyError) {
        console.error('플레이어 제거 실패:', destroyError)
      }
      playerRef.value = null
      if (playerElementId.includes('normal')) {
        playerReady.value = false
      }
    }
  }

  try {
    playerRef.value = new window.YT.Player(playerElementId, {
      height: playerHeight,
      width: '100%',
      videoId: videoId,
      playerVars: {
        autoplay: 0,
        controls: 1,
        modestbranding: 1,
        // rel: 0 제거 - YouTube 정책 준수: 관련 동영상 표시 필수
        showinfo: 0,
        iv_load_policy: 3
      },
      events: {
        onReady: onPlayerReady,
        onStateChange: onPlayerStateChange,
        onError: onPlayerError
      }
    })
  } catch (error) {
    console.error('YouTube Player 초기화 실패:', error)
  }
}

const onPlayerReady = (event) => {
  // 현재 활성 플레이어에서 발생한 이벤트만 처리
  const playerElementId = event.target.getIframe()?.id
  console.log('🎵 YouTube 플레이어 준비 완료:', playerElementId)

  // 전체화면 상태에 따라 해당 플레이어만 처리
  if (isFullscreen.value && !playerElementId?.includes('fullscreen')) {
    console.log('⏭️ 전체화면 모드 - 일반 플레이어 준비 이벤트 무시')
    return
  }
  if (!isFullscreen.value && !playerElementId?.includes('normal')) {
    console.log('⏭️ 일반 모드 - 전체화면 플레이어 준비 이벤트 무시')
    return
  }

  playerReady.value = true

  try {
    const videoData = event.target.getVideoData()
    console.log('🎵 로드된 비디오:', videoData.video_id, videoData.title)

    // Duration이 0이거나 설정되지 않았으면 YouTube에서 가져오기
    if (duration.value === 0) {
      try {
        const youtubeDuration = event.target.getDuration()
        if (youtubeDuration > 0) {
          duration.value = youtubeDuration
          console.log('🕒 YouTube Player Ready - Duration 설정:', duration.value)
        } else {
          console.log('⏳ YouTube Player Ready - Duration 아직 0, 나중에 재시도')
        }
      } catch (durationError) {
        console.error('YouTube duration 획득 실패:', durationError)
      }
    } else {
      console.log('🕒 YouTube Player Ready - 기존 Duration 사용:', duration.value)
    }

    // isPlaying이 true면 자동 재생 (전체재생, playSongAt 호출 시)
    if (isPlaying.value) {
      console.log('▶️ 플레이어 준비 완료 - isPlaying=true이므로 자동 재생')
      setTimeout(() => {
        try {
          if (typeof event.target.playVideo === 'function') {
            event.target.playVideo()
            console.log('✅ onPlayerReady에서 자동 재생 시작')
          }
        } catch (playError) {
          console.error('❌ onPlayerReady 자동 재생 실패:', playError)
        }
      }, 100)
    } else {
      console.log('⏸ 플레이어 준비 완료 - isPlaying=false이므로 재생 대기')
    }
  } catch (error) {
    console.error('플레이어 정보 획득 실패:', error)
  }
}

const onPlayerStateChange = (event) => {
  // 현재 활성 플레이어의 iframe ID 확인
  const playerElementId = event.target.getIframe()?.id

  // 전체화면 상태에 따라 해당 플레이어만 처리 (중복 이벤트 방지)
  if (isFullscreen.value && !playerElementId?.includes('fullscreen')) {
    console.log('⏭️ 전체화면 모드 - 일반 플레이어 이벤트 무시')
    return
  }
  if (!isFullscreen.value && !playerElementId?.includes('normal')) {
    console.log('⏭️ 일반 모드 - 전체화면 플레이어 이벤트 무시')
    return
  }

  const state = event.data
  console.log('🎵 YouTube Player 상태 변경:', state, 'currentSong:', currentSong.value.title, 'player:', playerElementId)

  if (state === window.YT.PlayerState.PLAYING) {
    console.log('▶️ 재생 시작')
    isPlaying.value = true

    // duration은 재생목록에서 설정된 값을 사용하므로 YouTube Player에서 업데이트하지 않음
    // try {
    //   const newDuration = event.target.getDuration()
    //   if (newDuration && newDuration !== duration.value) {
    //     duration.value = newDuration
    //     console.log('🕒 Duration 업데이트:', duration.value)
    //   }
    // } catch (error) {
    //   console.error('Duration 업데이트 실패:', error)
    // }

    startTimeUpdate()
    startPlayCountTimer() // 재생 시작 시 30초 타이머 시작
  } else if (state === window.YT.PlayerState.PAUSED) {
    console.log('⏸️ 일시정지')
    isPlaying.value = false
    stopTimeUpdate()
    stopPlayCountTimer() // 일시정지 시 타이머 중지
  } else if (state === window.YT.PlayerState.ENDED) {
    console.log('⏹️ 재생 완료')
    isPlaying.value = false
    stopTimeUpdate()
    stopPlayCountTimer() // 재생 완료 시 타이머 중지
    autoPlayNext()
  }
}

const onPlayerError = (event) => {
  const errorCode = event.data
  const errorMessages = {
    2: t('player.errorInvalidVideo'),
    5: t('player.errorHtml5'),
    100: t('player.errorNotFound'),
    101: t('player.errorEmbedNotAllowed'),
    150: t('player.errorEmbedNotAllowed')
  }

  const errorMessage = errorMessages[errorCode] || `${t('player.errorUnknown')} (${t('player.errorCode')}: ${errorCode})`
  console.error('YouTube Player Error:', errorMessage, event)

  // CORS 관련 오류가 아닌 경우에만 자동 다음 곡 재생
  if (errorCode !== 5) {
    autoPlayNext()
  }
}

// 시간 업데이트
let timeUpdateInterval = null
const startTimeUpdate = () => {
  stopTimeUpdate()
  timeUpdateInterval = setInterval(async () => {
    if (youtubePlayer.value && youtubePlayer.value.getCurrentTime) {
      const newCurrentTime = youtubePlayer.value.getCurrentTime()
      currentTime.value = newCurrentTime

      // duration이 0이면 YouTube Player에서 가져오기 시도
      if (duration.value === 0 && youtubePlayer.value.getDuration) {
        try {
          const youtubeDuration = youtubePlayer.value.getDuration()
          if (youtubeDuration > 0) {
            duration.value = youtubeDuration
            console.log('🕒 시간 업데이트 중 Duration 설정:', duration.value)
          }
        } catch (error) {
          // Duration 가져오기 실패는 무시 (자주 발생할 수 있음)
        }
      }

      // 구간 반복 체크
      handleTimeUpdate(newCurrentTime)

      // 현재 자막 업데이트 (타임스탬프 기반)
      updateCurrentLyric(newCurrentTime)

      // 누적 재생 시간 업데이트
      await updateAccumulatedPlayTime()
    }
  }, 1000)
}

const stopTimeUpdate = () => {
  if (timeUpdateInterval) {
    clearInterval(timeUpdateInterval)
    timeUpdateInterval = null
  }
}

// 재생 횟수 추적 함수
const startPlayCountTimer = () => {
  // 새로운 곡이 시작될 때 누적 시간 초기화
  const currentSongKey = getCurrentSongKey()
  if (currentSongKey !== lastSongKey.value) {
    accumulatedPlayTime.value = 0
    lastSongKey.value = currentSongKey
  }

  // 현재 시간 기록
  lastPlayTime.value = Date.now()
  console.log('⏰ 재생 시작 - 누적 재생 시간 추적 시작:', currentSong.value.title)
}

const stopPlayCountTimer = () => {
  if (playCountTimer.value) {
    clearTimeout(playCountTimer.value)
    playCountTimer.value = null
  }

  // 재생 정지 시 누적 시간 업데이트
  updateAccumulatedPlayTime()
  lastPlayTime.value = 0
}

// 곡 식별을 위한 키 생성
const getCurrentSongKey = () => {
  return `${currentSong.value.id || 'local'}_${currentSong.value.youtubeId || currentSong.value.videoId}`
}

// 데이터베이스 ID 추출 함수
const getCurrentSongDbId = () => {
  // 1. currentSong.value.id에서 먼저 확인
  if (currentSong.value.id && !isNaN(Number(currentSong.value.id))) {
    return Number(currentSong.value.id)
  }

  // 2. playlistSongs에서 해당하는 곡을 찾아 ID 확인
  const currentPlaylistSong = playlistSongs.value.find(song =>
    song.youtubeId === (currentSong.value.youtubeId || currentSong.value.videoId) ||
    song.videoId === (currentSong.value.youtubeId || currentSong.value.videoId)
  )

  if (currentPlaylistSong?.id && !isNaN(Number(currentPlaylistSong.id))) {
    return Number(currentPlaylistSong.id)
  }

  return null
}

// 재생 시간 누적 및 재생 횟수 체크
const updateAccumulatedPlayTime = async () => {
  if (lastPlayTime.value === 0) return

  const now = Date.now()
  const elapsed = (now - lastPlayTime.value) / 1000 // 초 단위

  // 5초 이상 차이나면 (일시정지나 점프) 무시
  if (elapsed > 5) {
    lastPlayTime.value = now
    return
  }

  accumulatedPlayTime.value += elapsed
  lastPlayTime.value = now

  // 30초 누적되면 재생횟수 카운트
  if (accumulatedPlayTime.value >= 30) {
    await tryUpdatePlayCount()
  }
}

// 실제 재생횟수 업데이트 시도
const tryUpdatePlayCount = async () => {
  const currentSongDbId = getCurrentSongDbId()
  const uniqueKey = getCurrentSongKey()

  if (!currentSongDbId || playCountCounted.value.has(uniqueKey)) {
    return
  }

  try {
    await songService.updatePlayCount(currentSongDbId)
    playCountCounted.value.add(uniqueKey)
    console.log(`✅ 재생 횟수 증가: ${currentSong.value.title} (누적시간: ${Math.round(accumulatedPlayTime.value)}초)`)

    // 카운트 후 누적시간 초기화 (같은 곡을 다시 30초 들으면 또 카운트)
    accumulatedPlayTime.value = 0
  } catch (error) {
    console.error('재생 횟수 업데이트 실패:', error)
  }
}

// 자동 다음 곡 재생
const autoPlayNext = () => {
  if (currentIndex.value < playlistSongs.value.length - 1) {
    nextSong()
  } else if (isRepeating.value && playlistSongs.value.length > 0) {
    playSongAt(0)
  }
}

// 프롭스 변경 감지 - 자동 추가 비활성화
watch(() => props.songs, (newSongs) => {
  console.log('🔄 props.songs 변경됨:', newSongs.length, '곡')
  console.log('🔄 현재 playlistSongs 길이:', playlistSongs.value.length)

  // 자동 추가 비활성화 - 사용자가 명시적으로 "전체 재생" 버튼을 눌렀을 때만 추가
  console.log('🚫 props.songs 자동 추가 비활성화됨 - 수동 재생만 허용')
  return

  // 원래 코드는 모두 비활성화됨 - 자동 추가 방지
}, { immediate: true })

// watch 제거 - 직접 저장 함수 호출로만 처리
let isLoadingFromLogin = false

// 재생목록 저장 함수 (수동 호출만)
const savePlaylistManually = async () => {
  if (authStore.isLoggedIn && !isLoadingFromLogin) {
    console.log('💾 수동 재생목록 저장:', playlistSongs.value.length, '곡')

    // 로컬스토리지 저장
    authStore.saveCurrentPlaylist(playlistSongs.value)

    // DB 저장 (빈 재생목록도 저장)
    try {
      await authStore.savePlaylistToDatabase(playlistSongs.value)
    } catch (error) {
      console.error('💾 DB 저장 실패:', error)
    }
  }
}

// 현재 곡 변경 감지
watch(currentSong, async (newSong, oldSong) => {
  if (newSong && (newSong.youtubeId || newSong.videoId)) {
    console.log('🎵 곡 변경 감지:', newSong.title)

    // 이전 곡과 다른 곡인 경우에만 재초기화
    const newVideoId = newSong.youtubeId || newSong.videoId
    const oldVideoId = oldSong?.youtubeId || oldSong?.videoId

    if (newVideoId !== oldVideoId) {
      console.log('🎵 새로운 곡으로 변경 - YouTube 플레이어 초기화')

      // 곡 변경 시 재생 횟수 타이머 리셋
      stopPlayCountTimer()
      playerReady.value = false // 플레이어 재초기화 시작 - 준비 상태 리셋

      await nextTick()
      setTimeout(async () => {
        try {
          // 기존 플레이어 상태 확인
          if (youtubePlayer.value && typeof youtubePlayer.value.getVideoData === 'function') {
            const currentVideoData = youtubePlayer.value.getVideoData()
            if (currentVideoData.video_id === newVideoId) {
              console.log('🎵 이미 올바른 영상이 로드됨')
              playerReady.value = true // 이미 로드됨 - 준비 완료 상태 유지
              return
            }
          }

          await initYouTubePlayer()
          console.log('🎵 YouTube 플레이어 초기화 완료:', newVideoId)
          console.log('🎯 곡 변경 완료 - 구간 정보 자동 업데이트됨')
        } catch (error) {
          console.error('YouTube 플레이어 초기화 실패:', error)
        }
      }, 100)
    } else {
      // 동일한 곡이지만 다른 이유로 곡이 설정된 경우
      console.log('🎯 동일 곡 - 구간 정보 자동 업데이트됨')
    }
  }
})

// 전체화면 모드 변경 감지 - 재생 시간 동기화
// 데스크탑 플레이어 표시 여부에 따른 body 스크롤 제어
watch(isDesktopPlayerVisible, (newValue) => {
  if (newValue) {
    document.body.style.overflow = 'hidden'
    // 플레이어가 표시될 때 높이 재계산 (DOM이 준비된 후)
    nextTick(() => {
      initPlayerPanelHeight()
    })
  } else {
    document.body.style.overflow = ''
  }
})

// 모바일 플레이어 표시 여부에 따른 body 스크롤 제어
watch(isMobilePlayerVisible, (newValue) => {
  if (newValue) {
    document.body.style.overflow = 'hidden'
  } else {
    document.body.style.overflow = ''
  }
})

watch(isFullscreen, async (newValue) => {
  emit('fullscreen-change', newValue)
  console.log('🖥️ 전체화면 전환:', newValue ? '전체화면 UI' : '일반 UI')

  // 전체화면 -> 일반 화면 전환 시 플레이어 패널 열기
  if (!newValue && props.globalMode) {
    if (window.innerWidth > 1024) {
      isDesktopPlayerVisible.value = true
    } else {
      isMobilePlayerVisible.value = true
    }
  }

  await nextTick()

  try {
    // 현재 플레이어에서 재생 시간 가져오기
    const fromPlayer = newValue ? youtubePlayerNormal.value : youtubePlayerFullscreen.value
    const toPlayer = newValue ? youtubePlayerFullscreen.value : youtubePlayerNormal.value

    if (!fromPlayer || !toPlayer) {
      console.warn('플레이어가 준비되지 않음')
      return
    }

    // 재생 시간 가져오기
    const currentTime = fromPlayer.getCurrentTime()
    const playerState = fromPlayer.getPlayerState()

    // 1. 이전 플레이어 정지
    fromPlayer.pauseVideo()

    // 2. 대상 플레이어로 재생 시간 동기화
    toPlayer.seekTo(currentTime, true)

    // 3. 재생 상태 동기화
    setTimeout(() => {
      if (playerState === 1) { // 재생 중이었으면
        isPlaying.value = true
        toPlayer.playVideo()
      } else { // 일시정지였으면
        isPlaying.value = false
        toPlayer.pauseVideo()
      }
    }, 100)
  } catch (error) {
    console.error('재생 시간 동기화 실패:', error)
  }
})

// 메소드
const playSongAt = async (index) => {
  try {
    console.log('곡 재생 시도:', index, playlistSongs.value.length)
    if (index >= 0 && index < playlistSongs.value.length) {
      currentIndex.value = index
      const newSong = playlistSongs.value[index]

      currentSong.value = {
        id: newSong.id, // Songs 테이블 ID
        youtubeId: newSong.youtubeId || newSong.videoId,
        videoId: newSong.youtubeId || newSong.videoId,
        title: newSong.title,
        artist: newSong.artist,
        thumbnailUrl: newSong.thumbnailUrl,
        duration: newSong.duration,
        sections: newSong.sections || [] // 구간선택 정보 포함
      }

      // 곡을 클릭했으므로 바로 재생 시작
      isPlaying.value = true

      // 새로운 곡 전환 시 재생목록의 duration 사용
      duration.value = newSong.duration || 0
      currentTime.value = 0

      // duration이 0이면 YouTube Player에서 가져오기 시도
      if (duration.value === 0 && youtubePlayer.value && playerReady.value) {
        try {
          const youtubeDuration = youtubePlayer.value.getDuration()
          if (youtubeDuration > 0) {
            duration.value = youtubeDuration
          }
        } catch (error) {
          console.error('YouTube Player Duration 가져오기 실패:', error)
        }
      }

      // 새로운 곡 재생 시 재생 시간을 0으로 초기화 및 재생 시작
      // cueVideoById 또는 플레이어 초기화 후 준비될 시간을 주기 위해 setTimeout 사용
      setTimeout(async () => {
        console.log('🎵 playSongAt setTimeout - playerReady:', playerReady.value, 'youtubePlayer:', !!youtubePlayer.value)

        // 플레이어가 없거나 준비 안 됐으면 초기화 시도
        if (!youtubePlayer.value || !playerReady.value) {
          console.warn('⚠️ 플레이어가 준비되지 않음. 초기화 시도...')
          await initYouTubePlayer()

          // 초기화 후 플레이어가 준비될 때까지 추가 대기
          await new Promise(resolve => setTimeout(resolve, 500))
        }

        if (youtubePlayer.value && playerReady.value) {
          try {
            // iframe 체크
            const iframe = youtubePlayer.value.getIframe?.()
            if (!iframe) {
              console.error('❌ iframe이 없습니다. isPlaying을 false로 되돌립니다.')
              isPlaying.value = false
              return
            }

            console.log('🎵 YouTube Player 재생 시작 시도 (playSongAt)')
            if (typeof youtubePlayer.value.seekTo === 'function') {
              youtubePlayer.value.seekTo(0)
            }
            if (typeof youtubePlayer.value.playVideo === 'function') {
              youtubePlayer.value.playVideo()
              console.log('✅ playVideo() 호출 완료 (playSongAt)')
            } else {
              console.warn('⚠️ playVideo 메서드 없음')
              isPlaying.value = false
            }
          } catch (playerError) {
            console.error('❌ 곡 재생 YouTube 플레이어 에러:', playerError)
            isPlaying.value = false
          }
        } else {
          console.warn('⚠️ 플레이어 초기화 실패 - isPlaying을 false로 되돌립니다.')
          isPlaying.value = false
        }
      }, 300)
    } else {
      console.warn('유효하지 않은 인덱스:', index)
    }
  } catch (error) {
    console.error('곡 재생 에러:', error)
  }
}

const togglePlay = async () => {
  try {
    console.log('재생/일시정지 토글')
    // 현재 곡이 없으면 첫 번째 곡 재생
    if (!currentSong.value.youtubeId && !currentSong.value.videoId) {
      playSongAt(0)
      return
    }
  } catch (error) {
    console.error('재생 토글 에러:', error)
  }

  if (youtubePlayer.value) {
    try {
      // iframe 존재 여부 먼저 확인
      const iframe = youtubePlayer.value.getIframe?.()
      if (!iframe) {
        console.warn('❌ YouTube iframe이 존재하지 않습니다. 플레이어를 재초기화합니다...')
        playerReady.value = false
        await initYouTubePlayer()
        // 재초기화 후 300ms 대기하여 플레이어 준비 완료
        setTimeout(() => {
          if (youtubePlayer.value) {
            try {
              youtubePlayer.value.playVideo()
              isPlaying.value = true
            } catch (e) {
              console.error('재초기화 후 재생 실패:', e)
            }
          }
        }, 300)
        return
      }

      // DOM 연결 상태 확인
      if (typeof youtubePlayer.value.getPlayerState !== 'function') {
        console.warn('플레이어가 DOM에 연결되지 않았습니다. 초기화를 기다리세요.')
        return
      }

      if (isPlaying.value && typeof youtubePlayer.value.pauseVideo === 'function') {
        youtubePlayer.value.pauseVideo()
        isPlaying.value = false
        // 일시정지 시 구간 선택 모드라면 구간 정보를 다시 표시
        console.log('일시정지 - 구간 선택 모드에서 구간 정보 표시')
      } else if (!isPlaying.value && typeof youtubePlayer.value.playVideo === 'function') {
        youtubePlayer.value.playVideo()
        isPlaying.value = true
        // 재생 시 구간 선택 모드에서도 실시간 자막으로 전환
        console.log('재생 시작 - 구간 선택 모드에서도 실시간 자막 표시')
      }
    } catch (playerError) {
      console.error('YouTube 플레이어 제어 에러:', playerError)
      // DOM 연결 에러면 playerReady 리셋하고 재초기화
      if (playerError.message && (playerError.message.includes('not attached') || playerError.message.includes('null'))) {
        console.warn('플레이어 DOM 연결 끊김 감지 - 재초기화 시도')
        playerReady.value = false
        await initYouTubePlayer()
      }
    }
  } else {
    console.warn('⚠️ youtubePlayer가 없습니다. 플레이어를 초기화합니다...')
    await initYouTubePlayer()
  }
}

const previousSong = () => {
  if (currentIndex.value > 0) {
    playSongAt(currentIndex.value - 1)
  }
}

const nextSong = () => {
  if (currentIndex.value < playlistSongs.value.length - 1) {
    playSongAt(currentIndex.value + 1)
  } else if (isRepeating.value) {
    playSongAt(0)
  }
}

const toggleRepeat = () => {
  isRepeating.value = !isRepeating.value
}

const removeSongFromPlayer = (index) => {
  try {
    console.log('곡 삭제 시도:', index)
    if (confirm(t('player.confirmRemoveSong'))) {
      const removedSong = playlistSongs.value[index]
      playlistSongs.value.splice(index, 1)

      if (index === currentIndex.value) {
        if (playlistSongs.value.length > 0) {
          const nextIndex = index < playlistSongs.value.length ? index : index - 1
          if (nextIndex >= 0) {
            playSongAt(nextIndex)
          } else {
            console.log('🔄 곡 제거 후 빈 currentSong 설정 (경우1)')
            currentSong.value = {}
            currentIndex.value = 0
          }
        } else {
          console.log('🔄 곡 제거 후 빈 currentSong 설정 (경우2)')
          currentSong.value = {}
          currentIndex.value = 0
        }
      } else if (index < currentIndex.value) {
        currentIndex.value--
      }

      // 수동 저장
      savePlaylistManually()

      // 이벤트 emit
      emit('songRemoved', { index, song: removedSong })
    }
  } catch (error) {
    console.error('곡 삭제 에러:', error)
  }
}

const seekTo = () => {
  if (youtubePlayer.value && playerReady.value) {
    try {
      if (typeof youtubePlayer.value.seekTo === 'function') {
        youtubePlayer.value.seekTo(currentTime.value, true)
      }
    } catch (playerError) {
      console.error('seekTo 에러:', playerError)
    }
  }
}

const formatTime = (seconds) => {
  if (!seconds || isNaN(seconds)) return '0:00'
  const totalSecs = Math.round(seconds) // 반올림으로 더 정확한 시간 표시
  const mins = Math.floor(totalSecs / 60)
  const secs = totalSecs % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

const toggleFullscreen = async () => {
  const wasPlaying = isPlaying.value
  const currentVideoTime = currentTime.value

  // 전환 전 현재 플레이어에서 상태 저장
  if (youtubePlayer.value) {
    try {
      if (typeof youtubePlayer.value.getCurrentTime === 'function') {
        currentTime.value = youtubePlayer.value.getCurrentTime()
      }
    } catch (error) {
      console.warn('현재 시간 저장 실패:', error)
    }
  }

  // 전체화면 모드 토글
  isFullscreen.value = !isFullscreen.value

  // 다음 틱에서 새 플레이어 확인 및 동기화
  await nextTick()

  setTimeout(() => {
    const targetPlayer = isFullscreen.value ? youtubePlayerFullscreen.value : youtubePlayerNormal.value

    if (!targetPlayer) {
      console.warn('⚠️ 전환된 플레이어가 초기화되지 않았습니다. 재초기화 시도...')
      initYouTubePlayer()
      return
    }

    try {
      // iframe 존재 확인
      const iframe = targetPlayer.getIframe?.()
      if (!iframe) {
        console.warn('⚠️ 전환된 플레이어의 iframe이 없습니다. 재초기화 시도...')
        initYouTubePlayer()
        return
      }

      // 재생 위치 복원
      if (typeof targetPlayer.seekTo === 'function') {
        targetPlayer.seekTo(currentVideoTime, true)
        console.log('✅ 전체화면 전환 - 재생 위치 복원:', formatTime(currentVideoTime))
      }

      // 재생 상태 복원
      if (wasPlaying && typeof targetPlayer.playVideo === 'function') {
        targetPlayer.playVideo()
        console.log('✅ 전체화면 전환 - 재생 재개')
      } else if (!wasPlaying && typeof targetPlayer.pauseVideo === 'function') {
        targetPlayer.pauseVideo()
        console.log('✅ 전체화면 전환 - 일시정지 유지')
      }
    } catch (error) {
      console.error('전체화면 전환 중 에러:', error)
    }
  }, 300)
}

const clearPlaylist = async () => {
  if (confirm(t('player.confirmClearPlaylist'))) {
    clearPlaylistSilent()

    // 로컬스토리지와 데이터베이스 모두 삭제
    if (authStore.isLoggedIn) {
      try {
        await authStore.saveCurrentPlaylist([])
        await authStore.savePlaylistToDatabase([])
        console.log('✅ 재생목록 삭제가 데이터베이스에 반영되었습니다')
      } catch (error) {
        console.error('❌ 데이터베이스 재생목록 삭제 실패:', error)
      }
    }
  }
}

const clearPlaylistSilent = () => {
  console.log('🔄 플레이리스트 클리어 - currentSong 및 Player 완전 초기화')
  playlistSongs.value = []
  currentSong.value = {}
  currentIndex.value = 0
  isPlaying.value = false
  currentTime.value = 0
  duration.value = 0
  playerReady.value = false // 플레이어 상태 리셋

  // YouTube Player 완전히 제거
  if (youtubePlayer.value) {
    try {
      if (typeof youtubePlayer.value.stopVideo === 'function') {
        youtubePlayer.value.stopVideo()
      }
      // Player 인스턴스 제거
      if (typeof youtubePlayer.value.destroy === 'function') {
        youtubePlayer.value.destroy()
        console.log('🗑️ YouTube Player 인스턴스 제거됨')
      }
    } catch (error) {
      console.error('플레이어 정지/제거 실패:', error)
    }
    youtubePlayer.value = null
  }

  // Silent 모드에서는 저장하지 않음
}


const addSong = (song) => {
  const exists = playlistSongs.value.some(s =>
    s.youtubeId === song.youtubeId || s.videoId === song.videoId
  )

  if (!exists) {
    playlistSongs.value.push(song)
    console.log('🎵 곡 추가됨:', song.title, '재생목록 길이:', playlistSongs.value.length)

    if (playlistSongs.value.length === 1) {
      console.log('🎵 첫 번째 곡 - 자동 재생 시작')
      playSongAt(0)
    }

    // 로그인 복원 중이 아닐 때만 저장
    if (!isLoadingFromLogin) {
      savePlaylistManually()
    }
  }
}

const addSongNext = (song) => {
  const exists = playlistSongs.value.some(s =>
    s.youtubeId === song.youtubeId || s.videoId === song.videoId
  )

  if (!exists) {
    const insertIndex = currentIndex.value + 1
    playlistSongs.value.splice(insertIndex, 0, song)

    if (playlistSongs.value.length === 1) {
      playSongAt(0)
    }

    // 로그인 복원 중이 아닐 때만 저장
    if (!isLoadingFromLogin) {
      savePlaylistManually()
    }
  }
}

// 여러 곡을 기존 목록 뒤에 추가하는 함수
const addMultipleSongs = (songs) => {
  console.log('🎵 여러 곡 추가:', songs.length, '곡')

  let addedCount = 0
  const startWasEmpty = playlistSongs.value.length === 0

  songs.forEach(song => {
    const exists = playlistSongs.value.some(s =>
      s.youtubeId === song.youtubeId || s.videoId === song.videoId
    )

    if (!exists) {
      playlistSongs.value.push(song)
      addedCount++
    }
  })

  console.log('🎵 실제 추가된 곡:', addedCount, '곡')

  // 처음 추가된 곡이 있고 이전에 재생목록이 비어있었다면 첫 곡 재생
  if (startWasEmpty && addedCount > 0) {
    playSongAt(0)
  }

  // 로그인 복원 중이 아닐 때만 저장
  if (!isLoadingFromLogin && addedCount > 0) {
    savePlaylistManually()
  }

  return addedCount
}

// 구간 반복 시스템 메서드들
const toggleSectionMode = async () => {
  const prevMode = sectionMode.value
  sectionMode.value = !sectionMode.value

  console.log('🎛️ 구간 설정 모드 토글:', prevMode ? '비활성화' : '활성화', '→', sectionMode.value ? '활성화' : '비활성화')

  if (!sectionMode.value) {
    // 구간 설정 모드 해제 시 구간도 해제
    console.log('🎛️ 구간 설정 모드 해제 - 구간 정리')
    clearSection()
  } else {
    // 구간 설정 모드 활성화 시 편집 상태 초기화 (새 구간 생성 모드)
    editingSectionId.value = null
    selectedSavedSection.value = null
    console.log('🎛️ 구간 설정 모드 활성화 - 새 구간 생성 준비')
  }
}

const getSectionHighlightStyle = () => {
  if (!selectedSection.value.start || !selectedSection.value.end || !duration.value) {
    return {}
  }

  const startPercent = (selectedSection.value.start / duration.value) * 100
  const widthPercent = ((selectedSection.value.end - selectedSection.value.start) / duration.value) * 100

  return {
    left: `${startPercent}%`,
    width: `${widthPercent}%`
  }
}

// 저장된 구간 마커 스타일
const getSectionMarkerStyle = (section) => {
  if (!section.start || !section.end || !duration.value) {
    return { display: 'none' }
  }

  const startPercent = (section.start / duration.value) * 100
  const widthPercent = ((section.end - section.start) / duration.value) * 100

  // 현재 활성화된 구간인지 확인
  const isActive = currentActiveSection.value?.id === section.id
  const isUpcoming = upcomingSection.value?.id === section.id

  const style = {
    left: `${startPercent}%`,
    width: `${widthPercent}%`
  }

  // 활성 구간이면 z-index를 높게 설정
  if (isActive || isUpcoming) {
    style.zIndex = '4'
  }

  return style
}

const startDragHandle = (handleType, event) => {
  if (event) {
    event.preventDefault()
    event.stopPropagation()
  }

  isDraggingHandle.value = true
  dragHandleType.value = handleType

  // 전체화면 여부에 따라 올바른 progress-bar 선택
  const progressBarSelector = isFullscreen.value
    ? '.fullscreen-player .progress-bar'
    : '.player-panel .progress-bar'

  const handleMouseMove = (e) => {
    if (!isDraggingHandle.value || !duration.value) return

    const progressBar = document.querySelector(progressBarSelector)
    if (!progressBar) {
      console.error('❌ progress-bar를 찾을 수 없음:', progressBarSelector)
      return
    }

    const rect = progressBar.getBoundingClientRect()

    // 터치와 마우스 모두 지원
    const clientX = e.touches ? e.touches[0].clientX : e.clientX
    const percent = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width))
    const newTime = percent * duration.value

    console.log('🖱️ 핸들 드래그:', dragHandleType.value, '위치:', formatTime(newTime), 'percent:', percent.toFixed(3))

    if (dragHandleType.value === 'start') {
      selectedSection.value.start = Math.max(0, Math.min(newTime, selectedSection.value.end - 1))
    } else if (dragHandleType.value === 'end') {
      selectedSection.value.end = Math.min(duration.value, Math.max(newTime, selectedSection.value.start + 1))
    }
  }

  const handleMouseUp = (e) => {
    console.log('🖱️ 핸들 드래그 종료:', dragHandleType.value)
    isDraggingHandle.value = false
    dragHandleType.value = null
    document.removeEventListener('mousemove', handleMouseMove)
    document.removeEventListener('mouseup', handleMouseUp)
    document.removeEventListener('touchmove', handleMouseMove)
    document.removeEventListener('touchend', handleMouseUp)
  }

  // 마우스와 터치 이벤트 모두 등록
  document.addEventListener('mousemove', handleMouseMove)
  document.addEventListener('mouseup', handleMouseUp)
  document.addEventListener('touchmove', handleMouseMove, { passive: false })
  document.addEventListener('touchend', handleMouseUp)

  console.log('🖱️ 핸들 드래그 시작:', handleType)
}

const playSection = () => {
  if (!youtubePlayer.value || !playerReady.value) return
  if (!selectedSection.value.start || !selectedSection.value.end) return

  try {
    youtubePlayer.value.seekTo(selectedSection.value.start)
    if (!isPlaying.value) {
      youtubePlayer.value.playVideo()
      isPlaying.value = true
    }
    console.log(`구간 재생: ${formatTime(selectedSection.value.start)} ~ ${formatTime(selectedSection.value.end)}`)
  } catch (error) {
    console.error('구간 재생 에러:', error)
  }
}

const toggleSectionRepeat = () => {
  sectionRepeatMode.value = !sectionRepeatMode.value
  console.log('구간 반복 모드:', sectionRepeatMode.value ? '활성화' : '비활성화')

  if (sectionRepeatMode.value) {
    playSection()
    // 구간 반복 시 구간 선택 모드에서도 실시간 자막으로 전환
    console.log('구간 반복 시작 - 구간 선택 모드에서도 실시간 자막 표시')
  } else {
    // 구간 반복 중지 시 구간 선택 모드라면 구간 정보를 다시 표시
    console.log('구간 반복 중지 - 구간 선택 모드에서 구간 정보 표시')
  }
}

const clearSection = () => {
  selectedSection.value = {
    start: null,
    end: null,
    name: ''
  }
  sectionRepeatMode.value = false
}


// 현재 재생 시간에 맞는 자막 업데이트
const updateCurrentLyric = (currentTimeInSeconds) => {
  if (!parsedLyrics.value || parsedLyrics.value.length === 0) {
    currentLyricLine.value = null
    currentLyricIndex.value = -1
    return
  }

  // 현재 시간보다 작거나 같은 가장 최근 자막 찾기
  let matchedLyric = null
  let matchedIndex = -1
  for (let i = parsedLyrics.value.length - 1; i >= 0; i--) {
    if (parsedLyrics.value[i].time <= currentTimeInSeconds) {
      matchedLyric = parsedLyrics.value[i]
      matchedIndex = i
      break
    }
  }

  // 매칭된 자막이 없으면 첫 번째 자막을 보여줌 (구간 시작 전에도 자막 표시)
  if (!matchedLyric && parsedLyrics.value.length > 0) {
    matchedLyric = parsedLyrics.value[0]
    matchedIndex = 0
  }

  currentLyricLine.value = matchedLyric
  currentLyricIndex.value = matchedIndex

  console.log('자막 업데이트:', {
    currentTime: currentTimeInSeconds,
    matchedLyric,
    matchedIndex,
    totalLyrics: parsedLyrics.value.length
  })
}

// 가사 파싱 함수 ([MM:SS] 형식의 타임스탬프 파싱)
const parseLyricsWithTimestamp = (lyricsText, pronunciationText = '') => {
  if (!lyricsText) return []

  const lines = lyricsText.split('\n')
  const pronunciationLines = pronunciationText ? pronunciationText.split('\n') : []
  const parsed = []

  lines.forEach((line, index) => {
    // [MM:SS] 또는 [M:SS] 형식 매칭
    const match = line.match(/^\[(\d{1,2}):(\d{2})\]\s*(.*)$/)
    if (match) {
      const minutes = parseInt(match[1])
      const seconds = parseInt(match[2])
      const text = match[3].trim()
      const timeInSeconds = minutes * 60 + seconds

      // 발음도 같은 타임스탬프 형식이면 파싱, 아니면 그냥 사용
      let transText = ''
      if (pronunciationLines[index]) {
        const transMatch = pronunciationLines[index].match(/^\[\d{1,2}:\d{2}\]\s*(.*)$/)
        transText = transMatch ? transMatch[1].trim() : pronunciationLines[index].trim()
      }

      parsed.push({
        time: timeInSeconds,
        text: text,
        trans_text: transText
      })
    }
  })

  return parsed.sort((a, b) => a.time - b.time)
}

// 현재 곡의 가사 로드 (구간반복 중 자막이 있는 것들 포함)
const loadCurrentLyrics = async () => {
  if (!canSaveSection.value) {
    currentLyrics.value = []
    console.log('가사 로드 실패: 곡 ID가 없음')
    return
  }

  try {
    console.log('가사 로드 시도 - 곡 ID:', currentSong.value.id)
    // 모든 사용자 구간을 가져와서 자막이 있는 것들만 필터링
    const allSections = await sectionService.getUserSongSections(currentSong.value.id)
    console.log('모든 구간 조회 결과:', allSections)

    const sectionsWithLyrics = allSections.filter(section => section.lyrics && section.lyrics.trim())
    console.log('자막이 있는 구간 필터링 결과:', sectionsWithLyrics)

    currentLyrics.value = sectionsWithLyrics
    console.log('자막이 있는 구간 로드됨:', sectionsWithLyrics.length, '개')
  } catch (error) {
    console.error('가사 로드 실패:', error)
    currentLyrics.value = []
  }
}

// 가사 저장 모달 열기
const openLyricsSaveModal = () => {
  if (!selectedSection.value.start || !selectedSection.value.end) return

  // 기본 가사 구간 이름 설정
  lyricsSaveForm.value = {
    name: `${currentSong.value.title || '곡'} 가사 ${currentLyrics.value.length + 1}구간`,
    lyrics: '',
    pronunciation: '',
    lineNumber: currentLyrics.value.length + 1,
    isPublic: false
  }

  showLyricsSaveModal.value = true
}

// 가사 저장 모달 닫기
const closeLyricsSaveModal = () => {
  showLyricsSaveModal.value = false
  editingLyricId.value = null
  lyricsSaveForm.value = {
    name: '',
    lyrics: '',
    pronunciation: '',
    lineNumber: 1,
    isPublic: false
  }
}

// 가사 저장 실행
const saveLyricsSection = async () => {
  const { name, lyrics, pronunciation, lineNumber, isPublic } = lyricsSaveForm.value

  if (!name.trim() || !lyrics.trim()) {
    alert(t('player.alertLyricsRequired'))
    return
  }

  try {
    // 서버에 가사 저장 또는 수정
    if (canSaveSection.value) {
      if (editingLyricId.value) {
        // 기존 가사 수정
        await sectionService.updateSection(editingLyricId.value, {
          type: 2, // SectionType.Lyrics
          name: name.trim(),
          startTime: selectedSection.value.start,
          endTime: selectedSection.value.end,
          lyrics: lyrics.trim(),
          pronunciation: pronunciation && pronunciation.trim() ? pronunciation.trim() : null,
          lineNumber: lineNumber,
          isPublic: isPublic
        })

        console.log('가사 구간 수정 성공')
        alert(t('player.alertLyricsUpdated', { name: name.trim() }))
      } else {
        // 새 가사 생성
        await sectionService.createLyricsSection({
          songId: currentSong.value.id,
          name: name.trim(),
          startTime: selectedSection.value.start,
          endTime: selectedSection.value.end,
          lyrics: lyrics.trim(),
          pronunciation: pronunciation && pronunciation.trim() ? pronunciation.trim() : null,
          lineNumber: lineNumber,
          isPublic: isPublic
        })

        console.log('가사 구간 저장 성공')
        alert(t('player.alertLyricsSaved', { name: name.trim() }))
      }

      // 가사 목록 새로고침
      await loadCurrentLyrics()
    }

    // 저장 성공 시 모달 닫기
    closeLyricsSaveModal()
    clearSection()
  } catch (error) {
    console.error('가사 구간 저장 에러:', error)
    alert(t('player.alertLyricsSaveFailed'))
  }
}

// 가사 관련 유틸리티 함수들
const isLyricActive = (lyric) => {
  return selectedSection.value.start === lyric.startTime &&
         selectedSection.value.end === lyric.endTime
}

const isLyricPlaying = (lyric) => {
  return currentTime.value >= lyric.startTime &&
         currentTime.value <= lyric.endTime
}

const jumpToLyric = (lyric) => {
  if (youtubePlayer.value && youtubePlayer.value.seekTo) {
    youtubePlayer.value.seekTo(lyric.startTime)
    console.log('가사 구간으로 이동:', lyric.name, formatTime(lyric.startTime))
  }
}

const jumpToTime = (time) => {
  if (youtubePlayer.value && youtubePlayer.value.seekTo) {
    youtubePlayer.value.seekTo(time)
    console.log('시간 이동:', formatTime(time))
  }
}

const editLyric = (lyric) => {
  // 편집 모드 설정
  editingLyricId.value = lyric.id

  // 가사 편집 모달 열기 (기존 데이터로 채우기)
  lyricsSaveForm.value = {
    name: lyric.name,
    lyrics: lyric.lyrics,
    pronunciation: lyric.pronunciation || '',
    lineNumber: lyric.lineNumber || 1,
    isPublic: lyric.isPublic || false
  }

  // 해당 구간을 선택된 구간으로 설정
  selectedSection.value = {
    start: lyric.startTime,
    end: lyric.endTime,
    name: lyric.name
  }

  showLyricsSaveModal.value = true
  console.log('가사 편집 모드:', lyric.name)
}

const deleteLyric = async (lyric) => {
  if (!confirm(t('player.confirmDeleteLyrics', { name: lyric.name }))) {
    return
  }

  try {
    await sectionService.deleteSection(lyric.id)
    await loadCurrentLyrics()
    console.log('가사 구간 삭제 성공:', lyric.name)
    alert(t('player.alertLyricsDeleted', { name: lyric.name }))
  } catch (error) {
    console.error('가사 구간 삭제 실패:', error)
    alert(t('player.alertLyricsDeleteFailed'))
  }
}

// 구간 저장 모달 열기
const openSectionSaveModal = () => {
  // editingSectionId로 기존 구간 편집인지 새 구간 생성인지 판단
  if (editingSectionId.value) {
    // 기존 구간 편집 - editingSectionId를 통해 구간 정보 가져오기
    const existingSection = savedSections.value.find(s => s.id === editingSectionId.value)
    if (existingSection) {
      // 기존 구간 편집 시에도 시간 정보가 변경되었으면 타이틀 업데이트
      const originalTimeRange = `${formatTime(existingSection.start)}~${formatTime(existingSection.end)}`
      const currentTimeRange = `${formatTime(selectedSection.value.start)}~${formatTime(selectedSection.value.end)}`

      let updatedName = existingSection.name
      if (originalTimeRange !== currentTimeRange) {
        // 기존 이름에서 시간 부분만 교체하거나, 새로운 시간으로 완전히 재생성
        if (existingSection.name.includes(originalTimeRange)) {
          updatedName = existingSection.name.replace(originalTimeRange, currentTimeRange)
        } else {
          // 기존 이름이 시간 정보를 포함하지 않으면 간소한 형식으로 추가
          updatedName = `구간반복 (${currentTimeRange})`
        }
      }

      sectionSaveForm.value = {
        name: updatedName,
        lyrics: existingSection.lyrics || '',
        pronunciation: existingSection.pronunciation || ''
      }
    } else {
      // 구간을 찾을 수 없으면 새 구간으로 처리
      editingSectionId.value = null
      sectionSaveForm.value = {
        name: `구간반복 (${formatTime(selectedSection.value.start)}~${formatTime(selectedSection.value.end)})`,
        lyrics: '',
        pronunciation: ''
      }
    }
  } else {
    // 새 구간 생성 - 이미 selectedSection에 start/end가 설정된 상태
    sectionSaveForm.value = {
      name: `구간반복 (${formatTime(selectedSection.value.start)}~${formatTime(selectedSection.value.end)})`,
      lyrics: '',
      pronunciation: ''
    }
  }

  showSectionSaveModal.value = true
}

// 구간 저장 모달 닫기
const closeSectionSaveModal = () => {
  showSectionSaveModal.value = false
  showLyricsExtractor.value = false // AI 가사 추출 UI도 닫기
  editingSectionId.value = null
  sectionSaveForm.value = {
    name: '',
    lyrics: '',
    pronunciation: ''
  }
}

// Whisper 서버 상태 확인
const checkWhisperServer = async () => {
  isCheckingWhisperServer.value = true
  try {
    const health = await getWhisperHealth()
    whisperServerAvailable.value = health.status === 'available'
    return whisperServerAvailable.value
  } catch (error) {
    console.warn('Whisper 서버 상태 확인 실패:', error)
    whisperServerAvailable.value = false
    return false
  } finally {
    isCheckingWhisperServer.value = false
  }
}

// AI 가사 추출 토글
const toggleLyricsExtractor = async () => {
  // 이미 확인 중이면 무시
  if (isCheckingWhisperServer.value) {
    return
  }

  // 서버 상태가 아직 확인되지 않았다면 먼저 확인
  if (whisperServerAvailable.value === null) {
    const isAvailable = await checkWhisperServer()
    if (!isAvailable) {
      alert('AI 가사 추출 서버가 실행되지 않았습니다.\n관리자에게 문의해주세요.')
      return
    }
  } else if (whisperServerAvailable.value === false) {
    alert('AI 가사 추출 서버가 실행되지 않았습니다.\n관리자에게 문의해주세요.')
    return
  }

  showLyricsExtractor.value = !showLyricsExtractor.value
}

// AI 가사 추출 완료 핸들러
const onLyricsExtracted = (lyrics) => {
  console.log('가사 추출 완료:', lyrics)

  // 시간 포맷 함수 (초 → MM:SS)
  const formatTimestamp = (seconds) => {
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `[${mins}:${secs.toString().padStart(2, '0')}]`
  }

  // 원본(text)과 변환(trans_text)을 각각 처리 (타임스탬프 포함)
  const originalTexts = []
  const transTexts = []

  lyrics.forEach(segment => {
    const timestamp = formatTimestamp(segment.start)
    if (segment.text) {
      originalTexts.push(`${timestamp} ${segment.text.trim()}`)
    }
    if (segment.trans_text) {
      transTexts.push(`${timestamp} ${segment.trans_text.trim()}`)
    }
  })

  // 가사 필드에 원본 입력 (타임스탬프 포함)
  sectionSaveForm.value.lyrics = originalTexts.join('\n')

  // 발음 필드에 변환된 텍스트 입력 (타임스탬프 포함, 모든 언어)
  sectionSaveForm.value.pronunciation = transTexts.join('\n')

  // 추출 UI 닫기
  showLyricsExtractor.value = false

  alert('AI 가사 추출이 완료되었습니다!\n"저장" 버튼을 눌러 구간을 저장해주세요.')
}

// AI 가사 추출 오류 핸들러
const onExtractionError = (error) => {
  console.error('가사 추출 오류:', error)

  // 에러 메시지에 따라 사용자 친화적인 안내 제공
  let errorMessage = '가사 추출에 실패했습니다.'

  if (error.message) {
    const msg = error.message.toLowerCase()
    if (msg.includes('서버에 연결할 수 없습니다') || msg.includes('서버가 실행되지 않았습니다')) {
      errorMessage = 'AI 가사 추출 서버가 실행되지 않았습니다.\n관리자에게 문의해주세요.'
      whisperServerAvailable.value = false
    } else if (msg.includes('시간이 초과')) {
      errorMessage = '가사 추출 시간이 초과되었습니다.\n구간 길이를 줄이거나 나중에 다시 시도해주세요.'
    } else {
      errorMessage = `가사 추출 실패: ${error.message}`
    }
  }

  alert(errorMessage)
}

// 구간 저장 실행
const saveSection = async () => {
  const { name, lyrics, pronunciation } = sectionSaveForm.value

  if (!name.trim()) return

  let localSection = null // 함수 최상위에서 선언

  try {
    if (editingSectionId.value) {
      // 기존 구간 편집
      const existingIndex = savedSections.value.findIndex(s => s.id === editingSectionId.value)
      if (existingIndex !== -1) {
        // 기존 구간 업데이트
        savedSections.value[existingIndex] = {
          ...savedSections.value[existingIndex],
          name: name.trim(),
          start: selectedSection.value.start,
          end: selectedSection.value.end,
          lyrics: lyrics && lyrics.trim() ? lyrics.trim() : null,
          pronunciation: pronunciation && pronunciation.trim() ? pronunciation.trim() : null,
          updatedAt: new Date()
        }

        localSection = savedSections.value[existingIndex] // 업데이트된 구간 참조

        // 선택된 구간도 업데이트
        if (selectedSavedSection.value && selectedSavedSection.value.id === editingSectionId.value) {
          selectedSavedSection.value = { ...savedSections.value[existingIndex] }
        }
      }
    } else {
      // 새 구간 생성
      localSection = {
        id: Date.now(),
        name: name.trim(),
        start: selectedSection.value.start,
        end: selectedSection.value.end,
        songId: currentSong.value.youtubeId || currentSong.value.videoId,
        songTitle: currentSong.value.title,
        songArtist: currentSong.value.artist,
        lyrics: lyrics && lyrics.trim() ? lyrics.trim() : null,
        pronunciation: pronunciation && pronunciation.trim() ? pronunciation.trim() : null,
        createdAt: new Date()
      }

      savedSections.value.push(localSection)
    }

    // localStorage에 저장
    localStorage.setItem('ttaechang_saved_sections', JSON.stringify(savedSections.value))

    // 로그인된 경우 서버에도 저장
    console.log('서버 저장 조건 확인 - authStore.isLoggedIn:', authStore.isLoggedIn)
    console.log('서버 저장 조건 확인 - currentSong.value:', currentSong.value)
    console.log('서버 저장 조건 확인 - currentSong.value.id:', currentSong.value.id)

    // 현재 곡의 DB ID 확인
    let songDbId = currentSong.value.id
    console.log('🔍 구간 저장 - currentSong.value.id:', songDbId)

    if (authStore.isLoggedIn && songDbId) {
      try {
        if (editingSectionId.value) {
          // 기존 구간 업데이트
          const originalSection = savedSections.value.find(s => s.id === editingSectionId.value)
          if (originalSection && originalSection.serverId) {
            await sectionService.updateSection(originalSection.serverId, {
              type: 1, // SectionType.Repeat
              name: name.trim(),
              startTime: selectedSection.value.start,
              endTime: selectedSection.value.end,
              lyrics: lyrics && lyrics.trim() ? lyrics.trim() : null,
              pronunciation: pronunciation && pronunciation.trim() ? pronunciation.trim() : null,
              description: `${currentSong.value.title} - ${currentSong.value.artist}`
            })
            console.log('구간 서버 업데이트 성공')
            alert(`구간 "${name.trim()}"이 수정되었습니다! (서버 동기화 완료)`)
          } else {
            // 로컬에만 있던 구간을 서버에 새로 생성
            const serverSection = await sectionService.createSection({
              songId: songDbId,
              type: 1, // SectionType.Repeat
              name: name.trim(),
              startTime: selectedSection.value.start,
              endTime: selectedSection.value.end,
              lyrics: lyrics && lyrics.trim() ? lyrics.trim() : null,
              pronunciation: pronunciation && pronunciation.trim() ? pronunciation.trim() : null,
              description: `${currentSong.value.title} - ${currentSong.value.artist}`
            })
            if (localSection) {
              localSection.serverId = serverSection.id
              localSection.isServerSection = true
              localStorage.setItem('ttaechang_saved_sections', JSON.stringify(savedSections.value))
            }
            console.log('로컬 구간 서버 생성 성공:', serverSection)
            alert(`구간 "${name.trim()}"이 수정되었습니다! (서버 동기화 완료)`)
          }
        } else {
          // 새 구간 생성
          console.log('새 구간 생성 - currentSong.value:', currentSong.value)
          console.log('새 구간 생성 - songId:', songDbId)

          const serverSection = await sectionService.createSection({
            songId: songDbId, // Songs 테이블의 실제 ID
            type: 1, // SectionType.Repeat
            name: name.trim(),
            startTime: selectedSection.value.start,
            endTime: selectedSection.value.end,
            lyrics: lyrics && lyrics.trim() ? lyrics.trim() : null,
            pronunciation: pronunciation && pronunciation.trim() ? pronunciation.trim() : null,
            description: `${currentSong.value.title} - ${currentSong.value.artist}`
          })

          // 서버 응답으로 로컬 섹션 업데이트
          if (localSection) {
            localSection.serverId = serverSection.id
            localSection.isServerSection = true
            localStorage.setItem('ttaechang_saved_sections', JSON.stringify(savedSections.value))
          }

          console.log('구간 서버 저장 성공:', serverSection)
          alert(`구간 "${name.trim()}"이 저장되었습니다! (서버 동기화 완료)`)
        }
      } catch (serverError) {
        console.warn('서버 저장/수정 실패, localStorage만 저장됨:', serverError)
        alert(`구간 "${name.trim()}"이 로컬에 저장되었습니다! (서버 저장 실패: 나중에 동기화됩니다)`)
      }
    } else {
      const actionText = editingSectionId.value ? '수정' : '저장'
      alert(`구간 "${name.trim()}"이 로컬에 ${actionText}되었습니다! (로그인 후 서버 동기화 가능)`)
    }

    console.log('구간 저장됨:', localSection)

    // 저장 후 구간 목록 새로고침
    await loadSavedSections()

    // 편집 상태 초기화
    editingSectionId.value = null

    // 가사가 있으면 실시간 자막용으로 파싱
    if (lyrics && lyrics.trim()) {
      parsedLyrics.value = parseLyricsWithTimestamp(lyrics, pronunciation)
      console.log('🎵 구간 저장 시 가사 파싱 완료:', {
        lyricsCount: parsedLyrics.value.length,
        firstLyric: parsedLyrics.value[0],
        lyrics,
        pronunciation
      })
    } else {
      console.log('⚠️ 구간 저장했지만 가사 없음')
    }

    // 저장 성공 시 모달 닫기
    closeSectionSaveModal()
  } catch (error) {
    console.error('구간 저장 에러:', error)
    alert('구간 저장에 실패했습니다. 다시 시도해주세요.')
  }
}

// YouTube 플레이어 시간 업데이트 처리에서 구간 반복 체크
const handleTimeUpdate = (currentTime) => {
  // 구간 반복 모드이고 종료 시점에 도달했을 때
  if (sectionRepeatMode.value &&
      selectedSection.value.start !== null &&
      selectedSection.value.end !== null &&
      currentTime >= selectedSection.value.end) {

    // 시작 지점으로 돌아가서 계속 재생
    setTimeout(() => {
      if (youtubePlayer.value && playerReady.value) {
        youtubePlayer.value.seekTo(selectedSection.value.start)
      }
    }, 100)
  }

  // 현재 활성화된 구간 추적
  updateActiveSections(currentTime)
}

// 현재 재생 시간에 따라 활성 구간 업데이트
const updateActiveSections = (currentTime) => {
  if (!currentSongSections.value.length) {
    currentActiveSection.value = null
    upcomingSection.value = null
    return
  }

  // 현재 재생 중인 구간 찾기 (2초 전부터 활성화 - 자막 영역 표시)
  const activeSection = currentSongSections.value.find(section =>
    currentTime >= (section.start - 2) && currentTime <= section.end
  )

  // 곧 시작될 구간 찾기 (5초 전부터 2초 전까지 - 떼창 예고 5,4,3,2초)
  const upcoming = currentSongSections.value.find(section =>
    currentTime >= (section.start - 5) && currentTime < (section.start - 2)
  )

  // 상태 업데이트
  if (activeSection !== currentActiveSection.value) {
    currentActiveSection.value = activeSection || null

    // 활성 구간이 바뀌면 해당 구간의 가사를 파싱
    if (activeSection && activeSection.lyrics && activeSection.lyrics.trim()) {
      parsedLyrics.value = parseLyricsWithTimestamp(activeSection.lyrics, activeSection.pronunciation)
      console.log('🎵 활성 구간 변경 - 가사 파싱:', {
        sectionName: activeSection.name,
        lyricsCount: parsedLyrics.value.length,
        firstLyric: parsedLyrics.value[0]
      })
    } else if (!activeSection) {
      // 활성 구간 없으면 자막 초기화
      parsedLyrics.value = []
      console.log('⚠️ 활성 구간 없음 - 자막 초기화')
    } else {
      // 활성 구간은 있지만 가사 없음
      parsedLyrics.value = []
      console.log('⚠️ 활성 구간에 가사 없음:', activeSection.name)
    }
  }

  if (upcoming !== upcomingSection.value) {
    upcomingSection.value = upcoming || null
  }
}

// 구간으로 점프 및 구간 선택 모드 활성화
const jumpToSection = (section) => {
  // 구간으로 이동하되 재생은 하지 않음
  if (youtubePlayer.value && playerReady.value) {
    youtubePlayer.value.seekTo(section.start)
    youtubePlayer.value.pauseVideo() // 재생 중지
  }

  // 재생 상태 업데이트
  isPlaying.value = false

  // 구간 선택 모드 활성화
  sectionMode.value = true

  // 해당 구간을 selectedSection에 로드
  selectedSection.value = {
    start: section.start,
    end: section.end,
    name: section.name || '',
    lyrics: section.lyrics || '',
    pronunciation: section.pronunciation || ''
  }

  // 구간을 선택된 상태로 설정 (기존 기능 유지)
  selectedSavedSection.value = section

  // 기존 구간이면 편집 모드로 설정
  editingSectionId.value = section.id

  // 가사가 있으면 실시간 자막용으로 파싱
  if (section.lyrics && section.lyrics.trim()) {
    parsedLyrics.value = parseLyricsWithTimestamp(section.lyrics, section.pronunciation)
    console.log('🎵 저장된 구간 불러옴 - 가사 파싱:', {
      lyricsCount: parsedLyrics.value.length,
      firstLyric: parsedLyrics.value[0]
    })
  } else {
    parsedLyrics.value = []
    console.log('⚠️ 저장된 구간에 가사 없음')
  }

  console.log('구간 클릭으로 선택됨 (일시정지):', section.name, `${formatTime(section.start)} - ${formatTime(section.end)}`)
}

const clearSectionSelection = () => {
  selectedSection.value.start = null
  selectedSection.value.end = null
  console.log('구간 선택 지움')
}

// 선택된 구간 삭제 확인
const confirmDeleteSelectedSection = () => {
  if (!editingSectionId.value) {
    console.warn('삭제할 구간이 선택되지 않았습니다.')
    return
  }

  const section = savedSections.value.find(s => s.id === editingSectionId.value)
  if (!section) {
    console.warn('삭제할 구간을 찾을 수 없습니다.')
    return
  }

  const confirmed = confirm(`구간 "${section.name}"을(를) 정말 삭제하시겠습니까?\n\n이 작업은 되돌릴 수 없습니다.`)

  if (confirmed) {
    deleteSelectedSection()
  }
}

// 선택된 구간 삭제 실행
const deleteSelectedSection = async () => {
  if (!editingSectionId.value) return

  try {
    const section = savedSections.value.find(s => s.id === editingSectionId.value)
    if (!section) return

    // 로컬에서 구간 삭제
    const index = savedSections.value.findIndex(s => s.id === editingSectionId.value)
    if (index !== -1) {
      savedSections.value.splice(index, 1)
      localStorage.setItem('ttaechang_saved_sections', JSON.stringify(savedSections.value))
    }

    // 서버에서도 삭제 (serverId가 있는 경우)
    if (authStore.isLoggedIn && section.serverId) {
      try {
        await sectionService.deleteSection(section.serverId)
        console.log('구간 서버 삭제 성공')
      } catch (serverError) {
        console.warn('서버 삭제 실패, 로컬에서만 삭제됨:', serverError)
      }
    }

    // 상태 초기화
    selectedSection.value.start = null
    selectedSection.value.end = null
    selectedSavedSection.value = null
    editingSectionId.value = null
    sectionMode.value = false

    // 구간 목록 새로고침
    await loadSavedSections()

    alert(`구간 "${section.name}"이 삭제되었습니다.`)
    console.log('구간 삭제 완료:', section.name)
  } catch (error) {
    console.error('구간 삭제 에러:', error)
    alert('구간 삭제에 실패했습니다. 다시 시도해주세요.')
  }
}

// 터치 이벤트 핸들러들 (간소화)
let longTouchTimer = null
const isLongTouch = ref(false)

const handleTouchStart = (event) => {
  console.log('📱 터치 시작 - 구간 모드:', sectionMode.value)

  if (!sectionMode.value) {
    console.log('❌ 구간 모드가 비활성화됨 - 터치 무시')
    return
  }

  event.preventDefault()
  isLongTouch.value = false

  console.log('📱 롱터치 타이머 시작 (1초)')
  // 1초 후 터치 위치에 구간 설정
  longTouchTimer = setTimeout(() => {
    isLongTouch.value = true
    console.log('📱 롱터치 감지됨 - 구간 설정 시작')
    // 터치 전용 구간 설정
    handleTouchSectionSelect(event)

    // 햅틱 피드백
    if ('vibrate' in navigator) {
      navigator.vibrate(100)
    }
  }, 1000)
}

// 터치용 구간 선택 함수
const handleTouchSectionSelect = (originalEvent) => {
  console.log('📱 구간 선택 함수 호출 - 구간모드:', sectionMode.value, '길이:', duration.value)

  if (!sectionMode.value) {
    console.log('❌ 구간 모드가 비활성화됨 - 중단')
    return
  }

  // duration이 0이면 YouTube Player에서 즉시 가져오기 시도
  if (duration.value === 0 && youtubePlayer.value && playerReady.value) {
    try {
      const youtubeDuration = youtubePlayer.value.getDuration()
      if (youtubeDuration > 0) {
        duration.value = youtubeDuration
        console.log('🕒 구간 선택 시 Duration 가져옴:', duration.value)
      }
    } catch (error) {
      console.error('구간 선택 시 Duration 가져오기 실패:', error)
    }
  }

  if (!duration.value) {
    console.log('❌ Duration이 0이어서 구간 선택 불가 - 중단')
    return
  }

  // 터치 위치 계산
  const touch = originalEvent.touches ? originalEvent.touches[0] : originalEvent
  const progressBar = originalEvent.target
  const rect = progressBar.getBoundingClientRect()
  const percent = (touch.clientX - rect.left) / rect.width
  const clickTime = Math.max(0, Math.min(percent * duration.value, duration.value))

  console.log('📱 터치 구간 선택:', formatTime(clickTime), '위치 퍼센트:', percent, '현재 구간 상태:', selectedSection.value)

  if (selectedSection.value.start === null) {
    // 시작 지점 설정
    selectedSection.value.start = clickTime
    console.log('✅ 구간 시작 지점 설정:', formatTime(clickTime))
  } else if (selectedSection.value.end === null) {
    // 종료 지점 설정
    if (clickTime > selectedSection.value.start) {
      selectedSection.value.end = clickTime
      console.log('✅ 구간 종료 지점 설정:', formatTime(clickTime))
      console.log('🎯 구간 완성:', `${formatTime(selectedSection.value.start)} ~ ${formatTime(selectedSection.value.end)}`)
    } else {
      // 시작점보다 이전을 터치한 경우 시작점을 다시 설정
      selectedSection.value.start = clickTime
      console.log('🔄 구간 시작 지점 재설정:', formatTime(clickTime))
    }
  } else {
    // 이미 구간이 설정된 경우 새로운 시작점으로 재설정
    selectedSection.value = {
      start: clickTime,
      end: null,
      name: ''
    }
    console.log('🆕 구간 새로 시작:', formatTime(clickTime))
  }
}

const handleTouchEnd = (event) => {
  console.log('📱 터치 종료 - 롱터치 여부:', isLongTouch.value)

  if (longTouchTimer) {
    console.log('📱 롱터치 타이머 취소')
    clearTimeout(longTouchTimer)
    longTouchTimer = null
  }
  isLongTouch.value = false
}

// 터치 드래그 핸들러들
let touchDragType = null
let touchDragging = false

const startTouchDrag = (type, event) => {
  event.preventDefault()
  event.stopPropagation()

  touchDragType = type
  touchDragging = true

  console.log('터치 드래그 시작:', type)

  // 햅틱 피드백
  if ('vibrate' in navigator) {
    navigator.vibrate(50)
  }
}

const handleTouchMove = (event) => {
  if (!touchDragging || !touchDragType) return

  event.preventDefault()

  const touch = event.touches[0]
  const progressContainer = event.target.closest('.progress-wrapper')
  const rect = progressContainer.getBoundingClientRect()

  // 터치 위치를 진행바 기준으로 계산
  const percent = Math.max(0, Math.min(1, (touch.clientX - rect.left) / rect.width))
  const newTime = percent * duration.value

  // 핸들 타입에 따라 시작점 또는 종료점 업데이트
  if (touchDragType === 'start') {
    selectedSection.value.start = Math.max(0, Math.min(newTime, selectedSection.value.end - 1))
  } else if (touchDragType === 'end') {
    selectedSection.value.end = Math.min(duration.value, Math.max(newTime, selectedSection.value.start + 1))
  }
}

const endTouchDrag = (event) => {
  if (touchDragging) {
    console.log('터치 드래그 종료:', touchDragType)

    // 햅틱 피드백
    if ('vibrate' in navigator) {
      navigator.vibrate(100)
    }
  }

  touchDragType = null
  touchDragging = false
}

// 구간 설정을 위한 더블클릭 핸들러
const handleProgressDoubleClick = (event) => {
  console.log('🖱️ 더블클릭 감지 - 구간모드:', sectionMode.value, '길이:', duration.value)

  if (!sectionMode.value) {
    console.log('❌ 구간 모드가 비활성화됨 - 더블클릭 무시')
    return
  }

  // duration이 0이면 YouTube Player에서 즉시 가져오기 시도
  if (duration.value === 0 && youtubePlayer.value && playerReady.value) {
    try {
      const youtubeDuration = youtubePlayer.value.getDuration()
      if (youtubeDuration > 0) {
        duration.value = youtubeDuration
        console.log('🕒 더블클릭 시 Duration 가져옴:', duration.value)
      }
    } catch (error) {
      console.error('더블클릭 시 Duration 가져오기 실패:', error)
    }
  }

  if (!duration.value) {
    console.log('❌ Duration이 0이어서 더블클릭 구간 설정 불가')
    return
  }

  const progressBar = event.target
  const rect = progressBar.getBoundingClientRect()
  const percent = (event.clientX - rect.left) / rect.width
  const clickTime = percent * duration.value

  console.log('🖱️ 더블클릭 위치:', formatTime(clickTime), '퍼센트:', percent)

  if (selectedSection.value.start === null) {
    // 시작 지점 설정
    selectedSection.value.start = clickTime
    console.log('구간 시작 지점 설정:', formatTime(clickTime))
  } else if (selectedSection.value.end === null) {
    // 종료 지점 설정
    if (clickTime > selectedSection.value.start) {
      selectedSection.value.end = clickTime
      console.log('구간 종료 지점 설정:', formatTime(clickTime))
    } else {
      // 시작점보다 이전을 클릭한 경우 시작점을 다시 설정
      selectedSection.value.start = clickTime
      console.log('구간 시작 지점 재설정:', formatTime(clickTime))
    }
  } else {
    // 이미 구간이 설정된 경우 새로운 시작점으로 재설정
    selectedSection.value = {
      start: clickTime,
      end: null,
      name: ''
    }
    console.log('새 구간 시작 지점 설정:', formatTime(clickTime))
  }
}

// 드래그 & 드롭 메서드들
const handleDragStart = (index, event) => {
  // 구간 모드일 때는 큐 드래그 완전 차단
  if (sectionMode.value) {
    event.preventDefault()
    event.stopPropagation()
    console.log('❌ 구간 모드 활성화 - 큐 드래그 차단')
    return false
  }

  try {
    console.log('드래그 시작:', index)
    draggedSongIndex.value = index
    event.dataTransfer.effectAllowed = 'move'
    event.dataTransfer.setData('text/html', '')
  } catch (error) {
    console.error('드래그 시작 에러:', error)
  }
}

const handleDragOver = (event) => {
  // 구간 모드일 때는 큐 드래그 완전 차단
  if (sectionMode.value) {
    return false
  }

  try {
    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'
  } catch (error) {
    console.error('드래그 오버 에러:', error)
  }
}

const handleDrop = (dropIndex, event) => {
  // 구간 모드일 때는 큐 드래그 완전 차단
  if (sectionMode.value) {
    event.preventDefault()
    event.stopPropagation()
    console.log('❌ 구간 모드 활성화 - 드롭 차단')
    return false
  }

  try {
    console.log('드롭 처리:', dropIndex)
    event.preventDefault()

    if (draggedSongIndex.value !== null && draggedSongIndex.value !== dropIndex) {
      const draggedSong = playlistSongs.value[draggedSongIndex.value]

      // 배열에서 드래그된 아이템 제거
      playlistSongs.value.splice(draggedSongIndex.value, 1)

      // 새 위치에 삽입
      const actualDropIndex = draggedSongIndex.value < dropIndex ? dropIndex - 1 : dropIndex
      playlistSongs.value.splice(actualDropIndex, 0, draggedSong)

      // 현재 재생 중인 곡의 인덱스 업데이트
      if (draggedSongIndex.value === currentIndex.value) {
        currentIndex.value = actualDropIndex
      } else if (draggedSongIndex.value < currentIndex.value && actualDropIndex >= currentIndex.value) {
        currentIndex.value--
      } else if (draggedSongIndex.value > currentIndex.value && actualDropIndex <= currentIndex.value) {
        currentIndex.value++
      }
    }

    draggedSongIndex.value = null
  } catch (error) {
    console.error('드롭 처리 에러:', error)
    draggedSongIndex.value = null
  }
}

const handleDragEnd = () => {
  // 구간 모드일 때는 큐 드래그 완전 차단
  if (sectionMode.value) {
    draggedSongIndex.value = null
    return false
  }

  try {
    console.log('드래그 종료')
    draggedSongIndex.value = null
  } catch (error) {
    console.error('드래그 종료 에러:', error)
  }
}

// 재생목록을 직접 설정하는 메서드 (저장 없이)
const setPlaylistSilent = (songs) => {
  console.log('🔄 setPlaylistSilent 호출:', songs.length, '곡')

  // 저장 방지 플래그 설정
  isLoadingFromLogin = true
  playlistSongs.value = [...songs]

  // 다음 프레임에서 플래그 리셋
  setTimeout(() => {
    isLoadingFromLogin = false
    console.log('🔄 setPlaylistSilent 플래그 리셋')
  }, 100)
}

// 게스트 사용자를 위한 데모 플레이리스트 로드
const loadDemoPlaylist = (demoPlaylist) => {
  try {
    console.log('🎵 게스트 데모 플레이리스트 로드:', demoPlaylist.title)

    if (!demoPlaylist.songs || demoPlaylist.songs.length === 0) {
      console.log('데모 플레이리스트에 곡이 없습니다.')
      return
    }

    // 플레이리스트 곡 설정
    playlistSongs.value = demoPlaylist.songs.map(song => ({
      ...song,
      youtubeId: song.youtubeId || song.videoId,
      artist: song.artist || song.channelTitle
    }))

    // 첫 번째 곡 자동 설정 (자동 재생은 하지 않음)
    if (playlistSongs.value[0]) {
      const firstSong = playlistSongs.value[0]

      currentSong.value = {
        id: firstSong.id,
        youtubeId: firstSong.youtubeId || firstSong.videoId,
        videoId: firstSong.youtubeId || firstSong.videoId,
        title: firstSong.title,
        artist: firstSong.artist,
        thumbnailUrl: firstSong.thumbnailUrl,
        duration: firstSong.duration,
        sections: firstSong.sections || []
      }
      currentIndex.value = 0
      sections.value = firstSong.sections || []

      // 플레이어 열기
      if (isMobile.value) {
        isMobilePlayerVisible.value = true
      } else {
        isDesktopPlayerVisible.value = true
      }

      console.log(`✅ 데모 플레이리스트 설정 완료: ${playlistSongs.value.length}곡`)
    }

  } catch (err) {
    console.error('데모 플레이리스트 로드 실패:', err)
  }
}

// 로그인 시 재생목록 복원하는 메서드
const loadPlaylistFromAuth = () => {
  // 이미 재생목록이 로드되어 있으면 중복 로드 방지
  if (playlistSongs.value.length > 0) {
    console.log('🔄 재생목록이 이미 로드되어 있음 - 중복 로드 방지')
    return
  }

  if (authStore.isLoggedIn && authStore.lastPlaylist && authStore.lastPlaylist.length > 0) {
    console.log('🔄 로그인 시 재생목록 복원:', authStore.lastPlaylist.length, '곡')

    // 로그인 시 복원 플래그 설정
    isLoadingFromLogin = true

    playlistSongs.value = [...authStore.lastPlaylist]

    // 첫 번째 곡 설정
    if (authStore.lastPlaylist[0]) {
      const firstSong = authStore.lastPlaylist[0]

      currentSong.value = {
        id: firstSong.id,
        youtubeId: firstSong.youtubeId || firstSong.videoId,
        videoId: firstSong.youtubeId || firstSong.videoId,
        title: firstSong.title,
        artist: firstSong.artist,
        thumbnailUrl: firstSong.thumbnailUrl,
        duration: firstSong.duration,
        sections: firstSong.sections || [] // 구간선택 정보 포함
      }
      currentIndex.value = 0
      console.log('🔄 첫 곡 설정:', firstSong.title)
    }

    // 플래그 리셋 (다음 프레임에서)
    setTimeout(() => {
      isLoadingFromLogin = false
      console.log('🔄 로그인 복원 완료 - 플래그 리셋')
    }, 500)
  }
}

// 화면이동/로그아웃 시 현재 재생목록을 authStore에 저장
const saveCurrentPlaylistToAuth = () => {
  if (authStore.isLoggedIn && playlistSongs.value.length > 0) {
    console.log('💾 현재 재생목록을 authStore에 저장:', playlistSongs.value.length, '곡')
    authStore.saveCurrentPlaylist(playlistSongs.value)
  }
}

// 컴포넌트 외부에서 접근 가능하도록 노출
// 플레이어 열기/닫기 함수
const openPlayer = () => {
  isDesktopPlayerVisible.value = true
  isMobilePlayerVisible.value = true
}

const closePlayer = () => {
  isDesktopPlayerVisible.value = false
  isMobilePlayerVisible.value = false
}

const isPlayerOpen = () => {
  return isDesktopPlayerVisible.value || isMobilePlayerVisible.value
}

defineExpose({
  addSong,
  addSongNext,
  addMultipleSongs,
  playSongAt,
  clearPlaylist,
  clearPlaylistSilent,
  setPlaylistSilent,
  loadPlaylistFromAuth,
  loadDemoPlaylist,
  saveCurrentPlaylistToAuth,
  openPlayer,
  closePlayer,
  isPlayerOpen
})

// 로그인 완료 시 재생목록 로드 이벤트 리스너
const handleAuthPlaylistLoaded = async () => {
  console.log('🔄 로그인 완료 이벤트 감지 - 재생목록 및 구간 정보 로드')
  loadPlaylistFromAuth()

  // 구간 정보도 다시 로드
  await loadSavedSections()
}

// 로그아웃 전 저장 이벤트 리스너
const handleAuthLogoutBeforeSave = async () => {
  console.log('🔄 로그아웃 전 재생목록 저장 이벤트 감지')
  if (authStore.isLoggedIn && playlistSongs.value.length > 0) {
    try {
      console.log('💾 로그아웃 전 현재 재생목록 저장:', playlistSongs.value.length, '곡')
      await authStore.savePlaylistToDatabase(playlistSongs.value)
      console.log('✅ 로그아웃 전 재생목록 저장 완료')
    } catch (error) {
      console.error('❌ 로그아웃 전 재생목록 저장 실패:', error)
    }
  }
}

// 로그아웃 시 재생목록 초기화 이벤트 리스너
const handleAuthLogout = () => {
  console.log('🔄 로그아웃 이벤트 감지 - 완전한 플레이어 초기화')

  // YouTube 플레이어 즉시 중지
  if (youtubePlayer.value && playerReady.value) {
    try {
      youtubePlayer.value.pauseVideo()
      youtubePlayer.value.stopVideo()
      console.log('✅ YouTube 플레이어 중지됨')
    } catch (e) {
      console.log('YouTube 플레이어 중지 실패:', e)
    }
  }

  // 모든 타이머 정리
  stopTimeUpdate()
  stopPlayCountTimer()

  // 저장 방지
  isLoadingFromLogin = true

  // 모든 상태 완전 초기화
  playlistSongs.value = []
  currentSong.value = {}
  currentIndex.value = 0
  isPlaying.value = false
  currentTime.value = 0
  duration.value = 0
  playerState.value = -1

  // 구간 정보도 초기화
  sections.value = []
  selectedSection.value = { start: null, end: null }

  // localStorage에서 플레이어 관련 모든 데이터 완전 삭제
  console.log('🗑️ localStorage 플레이어 데이터 완전 삭제')
  localStorage.removeItem('player_playback_state')
  localStorage.removeItem('ttaechang_saved_sections')
  localStorage.removeItem('ttaechang_current_playlist')
  localStorage.removeItem('player_current_song')
  localStorage.removeItem('player_settings')

  // sessionStorage에서도 삭제
  sessionStorage.removeItem('player_playback_state')
  sessionStorage.removeItem('player_current_song')

  console.log('🗑️ 재생목록 완전 삭제:', playlistSongs.value.length, '곡')
  console.log('🗑️ localStorage 플레이어 데이터 완전 정리 완료')

  setTimeout(() => {
    isLoadingFromLogin = false
    console.log('✅ 로그아웃 플레이어 초기화 완료')
  }, 100)
}


// 저장된 구간들을 localStorage에서 불러오기
const loadSavedSections = async () => {
  try {
    console.log('🎯 구간 로드 시작')

    // localStorage에서 로컬 구간 로드
    const saved = localStorage.getItem('ttaechang_saved_sections')
    if (saved) {
      savedSections.value = JSON.parse(saved)
      console.log('📱 localStorage에서 구간 로드됨:', savedSections.value.length, '개')
    } else {
      savedSections.value = []
      console.log('📱 localStorage에 저장된 구간 없음')
    }

    // 로그인된 경우 서버에서 구간 데이터도 가져오기
    if (authStore.isLoggedIn) {
      try {
        console.log('💾 서버에서 구간 데이터 로드 시작')
        const serverSections = await sectionService.getUserSections()
        console.log('💾 서버에서 구간 데이터 로드됨:', serverSections.length, '개')

        // 서버 구간을 로컬 형식으로 변환하고 중복 제거
        const existingServerIds = new Set(savedSections.value.filter(s => s.serverId).map(s => s.serverId))
        console.log('🔄 기존 서버 구간 ID들:', Array.from(existingServerIds))

        for (const serverSection of serverSections) {
          // 이미 로컬에 있는 서버 구간은 건너뛰기
          if (existingServerIds.has(serverSection.id)) {
            continue
          }

          const localSection = {
            id: `server_${serverSection.id}`, // 서버 섹션은 고유 ID 생성
            serverId: serverSection.id,
            name: serverSection.name,
            start: serverSection.startTime,
            end: serverSection.endTime,
            lyrics: serverSection.lyrics, // 가사 데이터 추가
            pronunciation: serverSection.pronunciation, // 발음 데이터 추가
            songId: serverSection.songYouTubeId || `db_${serverSection.songId}`, // YouTube ID 우선, 없으면 DB ID
            youtubeId: serverSection.songYouTubeId, // YouTube ID 별도 저장
            dbId: serverSection.songId, // DB ID 별도 저장
            songTitle: serverSection.songTitle || 'Unknown',
            songArtist: serverSection.songArtist || 'Unknown',
            createdAt: new Date(serverSection.createdAt),
            isServerSection: true
          }

          savedSections.value.push(localSection)
        }

        // 업데이트된 구간 목록을 localStorage에 저장
        localStorage.setItem('ttaechang_saved_sections', JSON.stringify(savedSections.value))
        console.log('✅ 구간 데이터 통합 완료. 총 구간 수:', savedSections.value.length)

      } catch (error) {
        console.error('서버 구간 로드 실패:', error)
        // 서버 로드 실패해도 로컬 구간은 사용 가능
      }
    } else {
      console.log('🔓 비로그인 상태 - 로컬 구간만 사용')
    }

    console.log('🎯 구간 로드 완료. 최종 구간 수:', savedSections.value.length)
  } catch (error) {
    console.error('전체 구간 로드 실패:', error)
  }
}

// 구간 삭제 함수
const deleteSavedSection = async (sectionId) => {
  const index = savedSections.value.findIndex(s => s.id === sectionId)
  if (index === -1) return

  const section = savedSections.value[index]

  // 삭제 확인 대화상자
  const confirmed = confirm(`"${section.name}" 구간을 정말로 삭제하시겠습니까?`)
  if (!confirmed) {
    return
  }

  try {
    // 로컬에서 먼저 삭제
    savedSections.value.splice(index, 1)
    localStorage.setItem('ttaechang_saved_sections', JSON.stringify(savedSections.value))

    // 선택된 구간이 삭제된 구간이면 상태 초기화
    if (selectedSavedSection.value && selectedSavedSection.value.id === sectionId) {
      selectedSavedSection.value = null
      isSavedSectionRepeating.value = false
      // 구간 반복이 실행 중이면 중지
      if (repeatIntervalId.value) {
        clearInterval(repeatIntervalId.value)
        repeatIntervalId.value = null
      }
    }

    // 서버 섹션인 경우 서버에서도 삭제
    if (authStore.isLoggedIn && section.serverId) {
      try {
        await sectionService.deleteSection(section.serverId)
        console.log('서버에서 구간 삭제 성공:', section.serverId)
      } catch (serverError) {
        console.warn('서버 삭제 실패:', serverError)
        // 서버 삭제 실패 시에도 로컬 삭제는 유지
      }
    }

    console.log('구간 삭제됨:', sectionId)
  } catch (error) {
    console.error('구간 삭제 에러:', error)
    // 에러 발생 시 삭제 취소
    savedSections.value.splice(index, 0, section)
    localStorage.setItem('ttaechang_saved_sections', JSON.stringify(savedSections.value))
  }
}


// 저장된 구간 불러오기 함수 (구간 적용)
const loadSavedSection = (section) => {
  selectedSection.value = {
    start: section.start,
    end: section.end,
    name: section.name
  }
  console.log('저장된 구간 불러옴:', section.name)
}

// 선택된 저장 구간 재생
const playSavedSection = () => {
  if (!selectedSavedSection.value) return

  // 구간을 현재 선택된 구간으로 설정
  selectedSection.value = {
    start: selectedSavedSection.value.start,
    end: selectedSavedSection.value.end,
    name: selectedSavedSection.value.name
  }

  // 구간 재생
  if (youtubePlayer.value && youtubePlayer.value.seekTo) {
    youtubePlayer.value.seekTo(selectedSavedSection.value.start)
    if (!isPlaying.value) {
      youtubePlayer.value.playVideo()
    }
    console.log('저장된 구간 재생:', selectedSavedSection.value.name)
  }
}

// 선택된 저장 구간 반복
const toggleSavedSectionRepeat = () => {
  if (!selectedSavedSection.value) return

  // 구간을 현재 선택된 구간으로 설정
  selectedSection.value = {
    start: selectedSavedSection.value.start,
    end: selectedSavedSection.value.end,
    name: selectedSavedSection.value.name
  }

  // 반복 모드 토글
  isSavedSectionRepeating.value = !isSavedSectionRepeating.value
  sectionRepeatMode.value = isSavedSectionRepeating.value

  if (isSavedSectionRepeating.value) {
    // 구간 시작점으로 이동하고 재생
    if (youtubePlayer.value && youtubePlayer.value.seekTo) {
      youtubePlayer.value.seekTo(selectedSavedSection.value.start)
      if (!isPlaying.value) {
        youtubePlayer.value.playVideo()
      }
    }
    console.log('저장된 구간 반복 시작:', selectedSavedSection.value.name)
  } else {
    console.log('저장된 구간 반복 중지:', selectedSavedSection.value.name)
  }
}

// 컴포넌트 생명주기
// 로그인 상태 변경 시 구간 정보 다시 로드
watch(() => authStore.isLoggedIn, async (isLoggedIn) => {
  console.log('🔄 로그인 상태 변경 감지:', isLoggedIn)
  if (isLoggedIn) {
    // 로그인 완료 시 구간 정보 다시 로드
    console.log('✅ 로그인 완료 - 구간 정보 다시 로드')
    await loadSavedSections()
  } else {
    // 로그아웃 시 플레이어 완전 초기화
    console.log('🗑️ 로그아웃 감지 - 플레이어 완전 초기화')
    playlistSongs.value = []
    currentSong.value = {}
    currentIndex.value = 0
    isPlaying.value = false
    currentTime.value = 0
    duration.value = 0
    playerState.value = -1
    sections.value = []
    selectedSection.value = { start: null, end: null }

    // localStorage 플레이어 데이터 완전 삭제
    localStorage.removeItem('player_playback_state')
    localStorage.removeItem('ttaechang_saved_sections')
    localStorage.removeItem('ttaechang_current_playlist')
    sessionStorage.removeItem('player_playback_state')

    console.log('🗑️ watch 로그아웃 - localStorage 플레이어 데이터 완전 삭제')
  }
})

onMounted(async () => {
  try {
    console.log('Player 컴포넌트 마운트됨')

    // 이벤트 리스너 등록
    window.addEventListener('beforeunload', handleBeforeUnload)
    window.addEventListener('auth-playlist-loaded', handleAuthPlaylistLoaded)
    window.addEventListener('auth-logout-before-save', handleAuthLogoutBeforeSave)
    window.addEventListener('auth-logout', handleAuthLogout)

    // 초기 플레이어 패널 높이 설정
    setTimeout(() => {
      initPlayerPanelHeight()
    }, 100)

    // 로그인 상태 확인 후 처리
    if (!authStore.isLoggedIn) {
      console.log('🗑️ 로그아웃 상태 감지 - 플레이어 완전 초기화')
      playlistSongs.value = []
      currentSong.value = {}
      currentIndex.value = 0
      isPlaying.value = false
      currentTime.value = 0
      duration.value = 0
      playerState.value = -1
      sections.value = []
      selectedSection.value = { start: null, end: null }

      // localStorage에서 플레이어 관련 데이터 완전 삭제
      localStorage.removeItem('player_playback_state')
      localStorage.removeItem('ttaechang_saved_sections')
      localStorage.removeItem('ttaechang_current_playlist')
      sessionStorage.removeItem('player_playback_state')

      console.log('🗑️ 로그아웃 상태 - localStorage 플레이어 데이터 완전 삭제')
    } else {
      // 저장된 구간들 로드
      await loadSavedSections()

      // 상태 복원 (재생 위치 등)
      restorePlaybackState()
    }
  } catch (error) {
    console.error('Player 컴포넌트 마운트 에러:', error)
  }
})

onUnmounted(() => {
  window.removeEventListener('beforeunload', handleBeforeUnload)
  window.removeEventListener('auth-playlist-loaded', handleAuthPlaylistLoaded)
  window.removeEventListener('auth-logout-before-save', handleAuthLogoutBeforeSave)
  window.removeEventListener('auth-logout', handleAuthLogout)
  stopTimeUpdate()
  stopPlayCountTimer() // 재생 횟수 타이머 정리

  // body overflow 복원
  document.body.style.overflow = ''

  // 상태 저장
  savePlaybackState()
})
</script>

<style scoped>
/* 기존 플레이어 패널 (기존 UI 스타일 유지) */
.player-panel {
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f172a 100%);
  border-radius: 20px;
  padding: 1.5rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  height: fit-content;
  position: sticky;
  /* top: 120px; */
  margin: 0;
}

/* 전체화면 모드일 때 패널 숨김 */
.player-panel.panel-fullscreen-mode {
  background: transparent;
  backdrop-filter: none;
  border: none;
  padding: 0;
}

/* 글로벌 모드 - App.vue에서 사용할 때 */
/* 데스크탑: 우측에서 슬라이드 인 */
.player-panel.player-global {
  position: fixed;
  /* top과 height는 JavaScript로 동적 설정 (fallback으로 기본값 제공) */
  top: 80px;
  height: calc(100vh - 160px);
  right: 0;
  width: 450px;
  overflow-y: auto;
  z-index: 999;
  transform: translateX(100%);
  transition: transform 0.3s ease-in-out;
  box-shadow: -4px 0 12px rgba(0, 0, 0, 0.3);
}

.player-panel.player-global.desktop-visible {
  transform: translateX(0);
}

/* 모바일: 하단에 숨김, 토글로 슬라이드 업 */
@media (max-width: 1024px) {
  .player-panel.player-global {
    top: auto;
    bottom: 0;
    right: 0;
    left: 0;
    width: 100%;
    height: 70vh;
    transform: translateY(100%);
    transition: transform 0.3s ease-in-out;
    border-radius: 20px 20px 0 0;
    box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.3);
  }

  .player-panel.player-global.desktop-visible {
    transform: translateY(100%);
  }

  .player-panel.player-global.mobile-visible {
    transform: translateY(0);
  }
}

/* 데스크탑 토글 버튼 */
.desktop-player-toggle {
  display: block;
  position: fixed;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1000;
  background: linear-gradient(45deg, #ff6b6b, #4ecdc4);
  color: white;
  border: none;
  padding: 1rem 0.5rem;
  border-radius: 8px 0 0 8px;
  font-weight: bold;
  cursor: pointer;
  box-shadow: -2px 2px 8px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
}

.desktop-player-toggle:hover {
  background: linear-gradient(45deg, #ff5252, #26c6da);
  padding-right: 0.75rem;
}

.desktop-player-toggle.active {
  right: 450px;
}

/* 모바일 토글 버튼 */
.mobile-player-toggle {
  display: none;
}

@media (max-width: 1024px) {
  .desktop-player-toggle {
    display: none;
  }

  .mobile-player-toggle {
    display: block;
    position: fixed;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    z-index: 1000;
    background: linear-gradient(45deg, #ff6b6b, #4ecdc4);
    color: white;
    border: none;
    padding: 0.75rem 2rem;
    border-radius: 20px 20px 0 0;
    font-weight: bold;
    cursor: pointer;
    box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.3);
    transition: all 0.3s ease;
  }

  .mobile-player-toggle:hover {
    background: linear-gradient(45deg, #ff5252, #26c6da);
  }

  .mobile-player-toggle.active {
    bottom: 70vh;
  }
}

/* 글로벌 모드 전체화면일 때 */
.player-panel.player-global.panel-fullscreen-mode {
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
  width: 100%;
  max-height: 100vh;
  border-radius: 0;
}

.player-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.player-title {
  font-size: 1.2rem;
  font-weight: bold;
  color: white;
}

.expand-btn {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  padding: 0.5rem;
  border-radius: 50%;
  cursor: pointer;
  font-size: 1rem;
  transition: all 0.3s ease;
}

.expand-btn:hover {
  background: rgba(255, 255, 255, 0.3);
}

.player-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.clear-all-btn {
  background: rgba(255, 99, 99, 0.2);
  border: none;
  color: white;
  padding: 0.5rem;
  border-radius: 50%;
  cursor: pointer;
  font-size: 1rem;
  transition: all 0.3s ease;
}

.clear-all-btn:hover {
  background: rgba(255, 99, 99, 0.4);
  transform: scale(1.1);
}

.video-placeholder {
  height: 225px;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 1rem;
}

.video-container {
  margin-bottom: 0;
  border-radius: 10px;
  overflow: hidden;
  height: 225px;
  position: relative;
}

.video-container.fullscreen {
  width: 100%;
  height: 800px; /* YouTube 전체 UI 표시를 위한 고정 높이 */
  position: relative;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
  border-radius: 15px;
  background: #000;
}

.video-container.fullscreen iframe {
  width: 100% !important;
  height: 100% !important;
}

.youtube-player {
  width: 100%;
  height: 100%;
  border-radius: 10px;
}

/* 전체화면 모드일 때 YouTube Player iframe 크기 */
.player-panel.panel-fullscreen-mode .youtube-player {
  width: 100%;
  height: 100%;
  border-radius: 15px;
}

/* 전체화면 YouTube Player */
.youtube-player.youtube-fullscreen {
  position: fixed !important;
  top: 80px !important;
  left: 50% !important;
  transform: translateX(-50%) !important;
  width: 90vw !important;
  max-width: 1600px !important;
  height: calc(90vh - 160px) !important;
  z-index: 10004 !important;
  border-radius: 15px !important;
}

.video-container.fullscreen .youtube-player {
  width: 100%;
  height: 100%;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

/* 자막 영역 */
.subtitle-area {
  margin-top: 1rem;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.subtitle-area.fullscreen {
  margin-top: 1rem;
  padding: 1rem;
}

.song-info {
  text-align: center;
  margin-top: 1rem; /* 동영상 영역과 간격 */
  margin-bottom: 1rem;
}

.current-song-title {
  font-size: 1.1rem;
  font-weight: bold;
  color: white;
  margin-bottom: 0.3rem;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.current-song-artist {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.8);
}

.player-controls {
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 1rem;
}

.control-btn {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  padding: 0.8rem;
  border-radius: 50%;
  cursor: pointer;
  font-size: 1.2rem;
  transition: all 0.3s ease;
  min-width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.control-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.1);
}

.control-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.control-btn.active {
  background: rgba(74, 144, 226, 1);
  border: 2px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 0 10px rgba(74, 144, 226, 0.6);
  transform: scale(1.05);
}

.control-btn.small {
  padding: 0.4rem 0.8rem;
  font-size: 0.8rem;
  min-width: auto;
  height: auto;
  border-radius: 15px;
}

.control-btn.danger {
  background: rgba(220, 53, 69, 0.7);
}

.control-btn.danger:hover {
  background: rgba(220, 53, 69, 0.9);
}

.control-btn.save-section {
  background: rgba(255, 193, 7, 0.7);
  color: #000;
}

.control-btn.save-section:hover {
  background: rgba(255, 193, 7, 0.9);
  transform: scale(1.1);
}

/* 구간 설정 모드 컨트롤 */
.section-mode-controls {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.8rem;
  justify-content: center;
  align-items: center;
}

.section-mode-btn {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  cursor: pointer;
  font-size: 0.85rem;
  transition: all 0.3s ease;
  white-space: nowrap;
}

.section-mode-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: translateY(-1px);
}

.section-mode-btn.active {
  background: rgba(78, 205, 196, 0.7);
  border-color: rgba(78, 205, 196, 0.9);
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 0.7; }
  50% { opacity: 1; }
}

.section-save-btn {
  background: rgba(255, 193, 7, 0.7);
  border: 1px solid rgba(255, 193, 7, 0.9);
  color: #000;
  padding: 0.4rem 0.8rem;
  border-radius: 15px;
  cursor: pointer;
  font-size: 0.8rem;
  transition: all 0.3s ease;
  font-weight: 600;
}

.section-save-btn:hover {
  background: rgba(255, 193, 7, 0.9);
  transform: translateY(-1px);
}

.section-repeat-btn {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 0.4rem 0.8rem;
  border-radius: 15px;
  cursor: pointer;
  font-size: 0.8rem;
  transition: all 0.3s ease;
}

.section-repeat-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: translateY(-1px);
}

.section-repeat-btn.active {
  background: rgba(74, 144, 226, 0.7);
  border-color: rgba(74, 144, 226, 0.9);
  animation: pulse 2s infinite;
}

.section-delete-btn {
  background: rgba(220, 53, 69, 0.6);
  border: 1px solid rgba(220, 53, 69, 0.8);
  color: white;
  padding: 0.4rem 0.8rem;
  border-radius: 15px;
  cursor: pointer;
  font-size: 0.8rem;
  transition: all 0.3s ease;
}

.section-delete-btn:hover {
  background: rgba(220, 53, 69, 0.8);
  transform: translateY(-1px);
}

/* 전체화면 모드 */
.section-mode-controls.fullscreen {
  margin-top: 1rem;
}

.section-mode-controls.fullscreen .section-mode-btn {
  font-size: 0.9rem;
  padding: 0.6rem 1.2rem;
}

.section-mode-controls.fullscreen .section-repeat-btn {
  font-size: 0.85rem;
  padding: 0.5rem 0.9rem;
}

.section-mode-controls.fullscreen .section-save-btn {
  font-size: 0.85rem;
  padding: 0.5rem 0.9rem;
}

.section-mode-controls.fullscreen .section-delete-btn {
  font-size: 0.85rem;
  padding: 0.5rem 0.9rem;
}

/* YouTube UI 간섭 방지를 위해 터치 오버레이 제거됨 */
/* 구간 설정은 프로그레스 바 클릭으로만 가능 */

.progress-section {
  margin-bottom: 1.5rem;
}

.progress-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.progress-label {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.8);
}


.progress-container {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.progress-wrapper {
  position: relative;
  flex: 1;
}

.progress-bar {
  width: 100%;
  height: 6px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 3px;
  outline: none;
  cursor: pointer;
  position: relative;
  z-index: 1;
}

.progress-bar::-webkit-slider-thumb {
  appearance: none;
  width: 18px;
  height: 18px;
  background: white;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.2s ease;
}

.progress-bar::-webkit-slider-thumb:hover {
  transform: scale(1.2);
}

.time-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-top: 0.3rem;
}

.time-display {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.8);
  min-width: 80px;
  text-align: left;
}

.section-info {
  font-size: 0.7rem;
  color: rgba(78, 205, 196, 0.9);
  text-align: right;
  flex: 1;
}

/* 구간 하이라이트 스타일 */
.section-highlight {
  position: absolute;
  top: 0;
  height: 6px;
  background: rgba(78, 205, 196, 0.6);
  border-radius: 3px;
  pointer-events: none;
  z-index: 2;
}

/* 구간 마커 컨테이너 */
.section-markers-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 6px;
  pointer-events: none;
  z-index: 1;
}

/* 구간 마커 스타일 */
.section-marker {
  position: absolute;
  top: 0;
  height: 6px;
  pointer-events: auto;
  cursor: pointer;
  transition: all 0.2s ease;
}

.section-marker:hover {
  transform: scaleY(1.5);
  z-index: 3;
}

.section-marker-start {
  position: absolute;
  left: 0;
  width: 1px;
  height: 100%;
  background: rgba(255, 193, 7, 0.9);
  border-radius: 1px 0 0 1px;
}

.section-marker-range {
  position: absolute;
  left: 1px;
  right: 1px;
  height: 100%;
  background: rgba(255, 193, 7, 0.3);
}

.section-marker-end {
  position: absolute;
  right: 0;
  width: 1px;
  height: 100%;
  background: rgba(255, 193, 7, 0.9);
  border-radius: 0 1px 1px 0;
}

/* 전체화면 모드 구간 마커 */
.section-marker.fullscreen {
  height: 8px;
}

.section-marker.fullscreen .section-markers-container {
  height: 8px;
}

.section-marker.fullscreen .section-marker-start,
.section-marker.fullscreen .section-marker-end {
  width: 2px;
}

.section-marker.fullscreen:hover {
  transform: scaleY(1.3);
}

/* 활성 구간 스타일 */
.section-marker.active .section-marker-start,
.section-marker.active .section-marker-end {
  background: rgba(255, 87, 34, 0.9);
  animation: activeGlow 1s infinite alternate;
}

.section-marker.active .section-marker-range {
  background: rgba(255, 87, 34, 0.5);
}

/* 예고 구간 스타일 */
.section-marker.upcoming .section-marker-start,
.section-marker.upcoming .section-marker-end {
  background: rgba(76, 175, 80, 0.9);
  animation: upcomingPulse 0.8s infinite;
}

.section-marker.upcoming .section-marker-range {
  background: rgba(76, 175, 80, 0.3);
}

@keyframes activeGlow {
  0% {
    box-shadow: 0 0 5px rgba(255, 87, 34, 0.5);
  }
  100% {
    box-shadow: 0 0 10px rgba(255, 87, 34, 0.8);
  }
}

@keyframes upcomingPulse {
  0%, 100% {
    transform: scaleY(1);
    opacity: 0.9;
  }
  50% {
    transform: scaleY(1.2);
    opacity: 1;
  }
}

/* 기존 떼창 알림 스타일 제거됨 - 실시간 자막으로 대체 */

/* 실시간 자막 스타일 - 타임스탬프 기반 좌우 분할 */
.realtime-subtitle-split {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  background: linear-gradient(135deg, rgba(0, 0, 0, 0.92) 0%, rgba(20, 20, 20, 0.96) 100%);
  padding: 1.2rem 1.5rem;
  border-radius: 12px;
  border-top: 3px solid rgba(255, 193, 7, 0.9);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.7);
  width: 100%;
  max-width: 100%;
}

.subtitle-column {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.subtitle-label {
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  opacity: 0.7;
  text-transform: uppercase;
  text-align: center;
}

.subtitle-column.original .subtitle-label {
  color: #4fc3f7;
}

.subtitle-column.translated .subtitle-label {
  color: #81c784;
}

.subtitle-text {
  color: white;
  font-size: 1.3rem;
  font-weight: 600;
  text-align: center;
  line-height: 1.5;
  text-shadow: 2px 2px 6px rgba(0, 0, 0, 0.9);
  animation: fadeInScale 0.3s ease-out;
}

@keyframes fadeInScale {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

/* 발음이 없을 때 원본만 중앙 배치 */
.realtime-subtitle-split:has(.subtitle-column:only-child) {
  grid-template-columns: 1fr;
}

/* 레거시 자막 스타일 (호환성) */
.realtime-subtitle {
  position: relative;
  background: linear-gradient(135deg, rgba(0, 0, 0, 0.9) 0%, rgba(20, 20, 20, 0.95) 100%);
  padding: 1rem 1.5rem;
  border-radius: 10px;
  border-top: 3px solid rgba(255, 193, 7, 0.8);
  animation: subtitleSlideIn 0.3s ease-out;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  margin: 0 auto;
  max-width: 100%;
}

/* 구간 선택 모드 자막 스타일 */
.realtime-subtitle.section-mode {
  background: linear-gradient(135deg, rgba(78, 205, 196, 0.9) 0%, rgba(56, 178, 170, 0.95) 100%);
  border-top: 2px solid rgba(78, 205, 196, 0.9);
}

.realtime-subtitle.section-mode .subtitle-header {
  font-size: 0.9rem;
  font-weight: 600;
  color: white;
  text-align: center;
  margin-bottom: 0.5rem;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.8);
}

.realtime-subtitle.fullscreen {
  padding: 1.5rem 2rem;
  border-radius: 15px;
  border-top-width: 4px;
  max-width: 90%;
}

.subtitle-content {
  font-size: 1.1rem;
  font-weight: 600;
  color: white;
  text-align: center;
  line-height: 1.4;
  margin-bottom: 0.3rem;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.8);
}

.realtime-subtitle.fullscreen .subtitle-content {
  font-size: 1.4rem;
  margin-bottom: 0.5rem;
}

.subtitle-pronunciation {
  font-size: 0.9rem;
  color: rgba(173, 216, 230, 0.9);
  text-align: center;
  font-style: italic;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.8);
}

.realtime-subtitle.fullscreen .subtitle-pronunciation {
  font-size: 1.1rem;
}

.realtime-subtitle.fullscreen.section-mode .subtitle-header {
  font-size: 1.2rem;
  margin-bottom: 0.8rem;
}

/* 전체화면 3줄 자막 (이전/현재/다음) */
.realtime-subtitle-3lines.fullscreen {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.5rem 2rem;
  border-radius: 15px;
  max-width: 90%;
  background: linear-gradient(135deg, rgba(33, 150, 243, 0.9) 0%, rgba(21, 101, 192, 0.95) 100%);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.4);
}

.realtime-subtitle-3lines .subtitle-line {
  display: flex;
  align-items: center;
}

.realtime-subtitle-3lines .subtitle-split {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  width: 100%;
}

.realtime-subtitle-3lines .subtitle-column {
  text-align: center;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.8);
}

/* 이전/다음 줄 스타일 (작은 글씨, 흐릿) */
.realtime-subtitle-3lines .subtitle-line.previous,
.realtime-subtitle-3lines .subtitle-line.next {
  opacity: 0.6;
}

.realtime-subtitle-3lines .subtitle-line.previous .subtitle-column,
.realtime-subtitle-3lines .subtitle-line.next .subtitle-column {
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.8);
}

/* 현재 줄 스타일 (굵고 큰 글씨) */
.realtime-subtitle-3lines .subtitle-line.current {
  opacity: 1;
}

.realtime-subtitle-3lines .subtitle-line.current .subtitle-column {
  font-size: 1.6rem;
  font-weight: 700;
  color: white;
}

/* 떼창 예고 자막 */
.subtitle-upcoming {
  position: relative;
  background: linear-gradient(135deg, rgba(76, 175, 80, 0.9) 0%, rgba(56, 142, 60, 0.95) 100%);
  padding: 1rem 1.5rem;
  border-radius: 10px;
  border-top: 3px solid rgba(76, 175, 80, 0.9);
  animation: upcomingPulse 1s infinite, subtitleSlideIn 0.3s ease-out;
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
  margin: 0 auto;
  max-width: 100%;
}

.subtitle-upcoming.fullscreen {
  padding: 1.5rem 2rem;
  border-radius: 15px;
  border-top-width: 4px;
  max-width: 90%;
}

.upcoming-text {
  font-size: 1rem;
  font-weight: 600;
  color: white;
  text-align: center;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.8);
}

.subtitle-upcoming.fullscreen .upcoming-text {
  font-size: 1.2rem;
}

@keyframes subtitleSlideIn {
  0% {
    transform: translateY(20px);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
}

/* 구간 설정 핸들 스타일 */
.section-handle {
  position: absolute;
  top: -10px;
  width: 20px;
  height: 24px;
  background: rgba(78, 205, 196, 0.9);
  border: 2px solid white;
  border-radius: 12px;
  cursor: grab;
  z-index: 100;
  transition: all 0.2s ease;
  transform: translateX(-50%);

  /* 터치 영역 확장 */
  padding: 8px;
  margin: -8px;

  /* 드래그 우선순위 보장 - 최우선 */
  pointer-events: auto !important;

  /* 브라우저 기본 터치 동작 차단 */
  touch-action: none;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  user-select: none;
}

.section-handle:hover {
  background: rgba(78, 205, 196, 1);
  transform: translateX(-50%) scale(1.2);
}

.section-handle:active {
  cursor: grabbing;
  transform: translateX(-50%) scale(1.3);
}

.section-handle.start {
  border-radius: 8px 4px 4px 8px;
  transform: translateX(calc(-100% + 8px)); /* margin-left: -8px 보정 */
}

.section-handle.start:hover {
  transform: translateX(calc(-100% + 8px)) scale(1.2);
}

.section-handle.start:active {
  transform: translateX(calc(-100% + 8px)) scale(1.3);
}

.section-handle.end {
  border-radius: 4px 8px 8px 4px;
  transform: translateX(8px); /* margin-left: -8px 보정 */
}

.section-handle.end:hover {
  transform: translateX(8px) scale(1.2);
}

.section-handle.end:active {
  transform: translateX(8px) scale(1.3);
}

/* 구간 반복 컨트롤 버튼들 */
.section-controls {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1rem;
  padding: 0.8rem;
  background: rgba(78, 205, 196, 0.1);
  border-radius: 12px;
  border: 1px solid rgba(78, 205, 196, 0.3);
}

.section-btn {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  padding: 0.5rem 0.8rem;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.75rem;
  transition: all 0.3s ease;
  min-height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
}

.section-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: translateY(-1px);
}

.section-btn.active {
  background: rgba(78, 205, 196, 0.8);
  color: white;
}

.section-btn.danger {
  background: rgba(220, 53, 69, 0.7);
}

.section-btn.danger:hover {
  background: rgba(220, 53, 69, 0.9);
}

.section-btn:disabled {
  background: rgba(108, 117, 125, 0.3);
  color: rgba(255, 255, 255, 0.5);
  cursor: not-allowed;
  transform: none;
}

.section-btn:disabled:hover {
  background: rgba(108, 117, 125, 0.3);
  transform: none;
}

.section-btn.primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.section-btn.primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

/* 모드 토글 버튼들 */
.section-header-buttons {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.mode-toggle-btn {
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: white;
  padding: 0.5rem;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  min-width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mode-toggle-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: translateY(-1px);
}

.mode-toggle-btn.active {
  background: rgba(78, 205, 196, 0.8);
  color: white;
  box-shadow: 0 2px 8px rgba(78, 205, 196, 0.4);
}

/* 체크박스 라벨 스타일 */
.checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  font-weight: 400;
}

.checkbox-label input[type="checkbox"] {
  width: auto;
  margin: 0;
}

/* 저장된 구간 목록 스타일 */
.saved-sections {
  margin-top: 1rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

/* 가사 표시 영역 스타일 */
.lyrics-display {
  margin-top: 1rem;
  background: linear-gradient(135deg, rgba(255, 182, 193, 0.1) 0%, rgba(186, 85, 211, 0.1) 100%);
  border-radius: 12px;
  padding: 1rem;
  border: 1px solid rgba(255, 182, 193, 0.2);
}

.lyrics-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.8rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.lyrics-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: rgba(255, 182, 193, 0.9);
}

.lyrics-list {
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  max-height: 300px;
  overflow-y: auto;
}

.lyric-item {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 10px;
  padding: 0.8rem;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;
  position: relative;
}

.lyric-item:hover {
  background: rgba(255, 182, 193, 0.1);
  transform: translateX(3px);
  border-color: rgba(255, 182, 193, 0.2);
}

.lyric-item.active {
  background: rgba(255, 182, 193, 0.2);
  border-color: rgba(255, 182, 193, 0.4);
  transform: translateX(3px);
}

.lyric-item.playing {
  background: linear-gradient(135deg, rgba(255, 215, 0, 0.2) 0%, rgba(255, 165, 0, 0.2) 100%);
  border-color: rgba(255, 215, 0, 0.5);
  box-shadow: 0 0 20px rgba(255, 215, 0, 0.3);
  animation: lyricPulse 2s ease-in-out infinite;
}

@keyframes lyricPulse {
  0%, 100% {
    box-shadow: 0 0 20px rgba(255, 215, 0, 0.3);
  }
  50% {
    box-shadow: 0 0 30px rgba(255, 215, 0, 0.5);
  }
}

.lyric-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.lyric-name {
  font-weight: 600;
  color: rgba(255, 182, 193, 0.9);
  font-size: 0.9rem;
}

.lyric-time {
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.6);
  background: rgba(255, 255, 255, 0.1);
  padding: 0.2rem 0.5rem;
  border-radius: 12px;
}

.lyric-text {
  color: white;
  font-size: 0.85rem;
  line-height: 1.4;
  margin-bottom: 0.5rem;
  white-space: pre-wrap;
}

.lyric-pronunciation {
  color: rgba(173, 216, 230, 0.8);
  font-size: 0.75rem;
  font-style: italic;
  margin-bottom: 0.5rem;
  padding: 0.3rem 0.5rem;
  background: rgba(173, 216, 230, 0.1);
  border-radius: 6px;
}

.lyric-actions {
  display: flex;
  gap: 0.3rem;
  justify-content: flex-end;
  margin-top: 0.5rem;
}

.lyric-action-btn {
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: white;
  padding: 0.3rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.8rem;
  transition: all 0.2s ease;
  opacity: 0.7;
}

.lyric-action-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: scale(1.1);
  opacity: 1;
}

.lyric-action-btn.danger:hover {
  background: rgba(220, 53, 69, 0.3);
}

/* 가사 없음 메시지 */
.no-lyrics {
  margin-top: 1rem;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 12px;
  padding: 2rem;
  border: 2px dashed rgba(255, 255, 255, 0.1);
}

.no-lyrics-message {
  text-align: center;
  color: rgba(255, 255, 255, 0.6);
}

.no-lyrics-icon {
  font-size: 3rem;
  display: block;
  margin-bottom: 1rem;
  opacity: 0.5;
}

.no-lyrics-message p {
  margin: 0.5rem 0;
  font-size: 0.9rem;
}

.no-lyrics-hint {
  font-size: 0.8rem;
  color: rgba(255, 182, 193, 0.7);
  font-style: italic;
}

.saved-sections-header {
  margin-bottom: 0.8rem;
}

.saved-sections-title {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 600;
}

.saved-sections-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: 200px;
  overflow-y: auto;
  /* 스크롤바 숨기기 */
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.saved-sections-list::-webkit-scrollbar {
  display: none;
}

.saved-section-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.6rem 0.8rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1px solid transparent;
}

.saved-section-item:hover {
  background: rgba(78, 205, 196, 0.2);
  border-color: rgba(78, 205, 196, 0.4);
  transform: translateX(3px);
}

.saved-section-info {
  flex: 1;
  min-width: 0;
}

.saved-section-name {
  font-size: 0.85rem;
  font-weight: 600;
  color: white;
  margin-bottom: 0.2rem;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.saved-section-details {
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.7);
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.saved-section-song {
  color: rgba(78, 205, 196, 0.9);
}


.delete-section-btn.small {
  padding: 0.4rem 0.8rem;
  font-size: 0.75rem;
  min-width: auto;
  height: auto;
}


.lyrics-btn {
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 0.6rem 1.2rem;
  border-radius: 25px;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 500;
  transition: all 0.3s ease;
  min-width: 100px;
}

.lyrics-btn:hover {
  background: rgba(255, 255, 255, 0.25);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(255, 255, 255, 0.2);
}

.lyrics-btn.primary {
  background: rgba(78, 205, 196, 0.8);
  border-color: rgba(78, 205, 196, 0.9);
}

.lyrics-btn.primary:hover {
  background: rgba(78, 205, 196, 0.9);
  box-shadow: 0 4px 12px rgba(78, 205, 196, 0.3);
}

.lyrics-btn.active {
  background: rgba(255, 215, 0, 0.8);
  border-color: rgba(255, 215, 0, 0.9);
  color: #2c3e50;
  font-weight: 600;
}

.lyrics-btn.active:hover {
  background: rgba(255, 215, 0, 0.9);
  box-shadow: 0 4px 12px rgba(255, 215, 0, 0.3);
}

.lyrics-btn.danger {
  background: rgba(231, 76, 60, 0.8);
  border-color: rgba(231, 76, 60, 0.9);
}

.lyrics-btn.danger:hover {
  background: rgba(231, 76, 60, 0.9);
  box-shadow: 0 4px 12px rgba(231, 76, 60, 0.3);
}

/* 스크롤바 스타일 */
.saved-section-lyrics::-webkit-scrollbar,
.saved-section-pronunciation::-webkit-scrollbar {
  width: 6px;
}

.saved-section-lyrics::-webkit-scrollbar-track,
.saved-section-pronunciation::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
}

.saved-section-lyrics::-webkit-scrollbar-thumb,
.saved-section-pronunciation::-webkit-scrollbar-thumb {
  background: rgba(173, 216, 230, 0.6);
  border-radius: 10px;
  transition: background 0.3s ease;
}

.saved-section-lyrics::-webkit-scrollbar-thumb:hover,
.saved-section-pronunciation::-webkit-scrollbar-thumb:hover {
  background: rgba(173, 216, 230, 0.8);
}

.saved-section-lyrics {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.95);
  font-weight: 500;
  margin-top: 0.6rem;
  padding: 0.8rem 1rem;
  background: linear-gradient(135deg, rgba(255, 182, 193, 0.2) 0%, rgba(186, 85, 211, 0.1) 100%);
  border-radius: 10px;
  border: 1px solid rgba(255, 182, 193, 0.3);
  border-left: 4px solid rgba(255, 182, 193, 0.8);
  line-height: 1.5;
  white-space: pre-wrap;
  box-shadow: 0 2px 8px rgba(255, 182, 193, 0.15);
  transition: all 0.3s ease;
  max-height: 120px;
  overflow-y: auto;
  word-break: break-word;
}

.saved-section-item:hover .saved-section-lyrics {
  background: linear-gradient(135deg, rgba(255, 182, 193, 0.3) 0%, rgba(186, 85, 211, 0.2) 100%);
  border-color: rgba(255, 182, 193, 0.5);
  box-shadow: 0 4px 12px rgba(255, 182, 193, 0.25);
  transform: translateY(-1px);
}

.saved-section-pronunciation {
  font-size: 0.75rem;
  color: rgba(173, 216, 230, 0.9);
  margin-top: 0.4rem;
  padding: 0.4rem 0.6rem;
  background: rgba(173, 216, 230, 0.1);
  border-radius: 6px;
  border: 1px solid rgba(173, 216, 230, 0.2);
  font-style: italic;
  font-weight: 400;
  max-height: 60px;
  overflow-y: auto;
  word-break: break-word;
}

.delete-section-btn {
  background: rgba(220, 53, 69, 0.7);
  border: none;
  color: white;
  padding: 0.3rem;
  border-radius: 50%;
  cursor: pointer;
  font-size: 0.7rem;
  transition: all 0.3s ease;
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-left: 0.5rem;
}

.delete-section-btn:hover {
  background: rgba(220, 53, 69, 0.9);
  transform: scale(1.1);
}

.playlist-songs {
  max-height: 300px; /* 광고 영역(90px) 확보를 위해 줄임 */
  overflow-y: auto;
  /* 스크롤바 숨기기 - 웹킷 브라우저 */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* Internet Explorer 10+ */
}

/* 모바일에서 광고 영역(50px) 확보 */
@media (max-width: 768px) {
  .playlist-songs {
    max-height: 200px;
  }
}

.playlist-songs::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Opera */
}

.playlist-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.playlist-title {
  font-size: 1rem;
  font-weight: bold;
  color: white;
}

.playlist-controls {
  display: flex;
  gap: 0.5rem;
}

.songs-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  touch-action: pan-y; /* 세로 스크롤만 허용 */
}

.song-item {
  display: flex;
  align-items: center;
  padding: 0.8rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;
  position: relative;
  z-index: 1;
  user-select: none;
  -webkit-user-select: none;
  touch-action: pan-y; /* 세로 스크롤만 허용 */
}

/* 구간 모드일 때 큐 아이템 터치/드래그 완전 차단 */
.song-item.section-mode-active {
  pointer-events: none !important;
  cursor: default !important;
  opacity: 0.5;
  touch-action: none !important; /* 모든 터치 동작 차단 */
}

/* 프로그레스 바도 항상 작동해야 함 */
.progress-bar-container {
  pointer-events: auto !important;
  touch-action: none;
}

/* 프로그레스 바 자체 */
.progress-bar {
  touch-action: none;
}

.song-item:hover {
  background: rgba(255, 255, 255, 0.15);
  transform: translateX(5px);
}

.song-item.active {
  background: rgba(74, 144, 226, 0.4);
  border-color: rgba(74, 144, 226, 0.6);
}

.song-item.dragging {
  opacity: 0.5;
  transform: rotate(5deg);
}

.song-number {
  width: 30px;
  text-align: center;
  font-weight: bold;
  color: rgba(255, 255, 255, 0.8);
  margin-right: 0.8rem;
}

.song-details {
  flex: 1;
  min-width: 0;
}

.song-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: white;
  margin-bottom: 0.2rem;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.song-artist {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.7);
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.song-duration {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.6);
  margin-right: 0.5rem;
  min-width: 40px;
  text-align: right;
}

.remove-song-btn {
  background: rgba(220, 53, 69, 0.7);
  border: none;
  color: white;
  padding: 0.3rem;
  border-radius: 50%;
  cursor: pointer;
  font-size: 0.8rem;
  transition: all 0.3s ease;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.remove-song-btn:hover {
  background: rgba(220, 53, 69, 0.9);
  transform: scale(1.1);
}

/* 전체화면 플레이어 스타일 */
.fullscreen-player {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f172a 100%);
  z-index: 10002;
  overflow: hidden;
  box-sizing: border-box;
}

.fullscreen-container {
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
  box-sizing: border-box;
}

.fullscreen-header {
  position: relative;
  width: 100%;
  height: 80px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 2rem;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
}

.fullscreen-controls {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-shrink: 0;
}

.close-btn-container {
  position: absolute;
  top: 80px;
  right: 20px;
  z-index: 1000;
}

.fullscreen-main {
  display: flex;
  flex: 1;
  gap: 1rem;
  padding: 1rem;
  overflow-y: auto; /* 일시정지 시 YouTube UI가 확장되면 스크롤 */
  overflow-x: hidden;
  box-sizing: border-box;
  height: calc(100vh - 80px);
}

.fullscreen-player-area {
  flex: 7;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  max-width: 70%;
  overflow-y: auto; /* 자막이 길어지면 전체 영역 스크롤 */
  overflow-x: hidden;
  box-sizing: border-box;
  height: 100%;
}

.fullscreen-playlist-area {
  flex: 3;
  display: flex;
  flex-direction: column;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  max-width: 30%;
  overflow: hidden;
  box-sizing: border-box;
  height: 100%;
}

.fullscreen-title {
  font-size: 1.5rem;
  font-weight: bold;
  color: white;
}

.close-btn {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  padding: 0.8rem;
  border-radius: 50%;
  cursor: pointer;
  font-size: 1.2rem;
  transition: all 0.3s ease;
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.1);
}

.fullscreen-content {
  flex: 1;
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 2rem;
  padding: 2rem;
  overflow: hidden;
}

.fullscreen-video-section {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  overflow: hidden;
}

.fullscreen-video-placeholder {
  height: 400px;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.7);
  font-size: 1.2rem;
}

.fullscreen-video-container {
  border-radius: 15px;
  overflow: hidden;
  height: 800px; /* 데스크톱: YouTube 전체 UI 표시 (동영상 더보기 포함) */
}

.fullscreen-video-container .youtube-player {
  height: 100%;
  min-height: 800px;
}

.fullscreen-player .youtube-player {
  width: 100%;
  height: 100%;
  border-radius: 10px;
  flex: 1;
}

.fullscreen-player .youtube-player iframe {
  width: 100% !important;
  height: 100% !important;
  border-radius: 10px;
}

.fullscreen-song-info {
  text-align: center;
  margin-bottom: 0.1rem;
  flex-shrink: 0;
  order: -2;
}

.fullscreen-song-title {
  font-size: 1rem;
  font-weight: bold;
  color: white;
  margin-bottom: 0.1rem;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.fullscreen-song-artist {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.8);
}

.fullscreen-controls {
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 0.1rem;
  flex-shrink: 0;
  order: -1;
}

.fullscreen-control-btn {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  padding: 0.6rem;
  border-radius: 50%;
  cursor: pointer;
  font-size: 1.2rem;
  transition: all 0.3s ease;
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.fullscreen-control-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.1);
}

.fullscreen-control-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.fullscreen-control-btn.active {
  background: rgba(74, 144, 226, 0.8);
}

.fullscreen-progress-section {
  margin-top: auto;
}

.fullscreen-progress-container {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.fullscreen-progress-bar {
  flex: 1;
  height: 8px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 4px;
  outline: none;
  cursor: pointer;
}

.fullscreen-progress-bar::-webkit-slider-thumb {
  appearance: none;
  width: 24px;
  height: 24px;
  background: white;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.2s ease;
}

.fullscreen-progress-bar::-webkit-slider-thumb:hover {
  transform: scale(1.2);
}

.fullscreen-time-display {
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.8);
  min-width: 120px;
  text-align: right;
}

.fullscreen-playlist-section {
  display: flex;
  flex-direction: column;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 1.5rem;
  overflow: hidden;
}

.fullscreen-playlist-header {
  margin-bottom: 1rem;
}

.fullscreen-playlist-title {
  font-size: 1.2rem;
  font-weight: bold;
  color: white;
}

.fullscreen-songs-list {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  /* 스크롤바 숨기기 - 웹킷 브라우저 */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* Internet Explorer 10+ */
}

.fullscreen-songs-list::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Opera */
}

.fullscreen-song-item {
  display: flex;
  align-items: center;
  padding: 0.8rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.fullscreen-song-item:hover {
  background: rgba(255, 255, 255, 0.15);
  transform: translateX(5px);
}

.fullscreen-song-item.active {
  background: rgba(74, 144, 226, 0.4);
  border-color: rgba(74, 144, 226, 0.6);
}

.fullscreen-song-item.dragging {
  opacity: 0.5;
  transform: rotate(5deg);
}

/* 모바일 대응 */
@media (max-width: 768px) {
  .fullscreen-content {
    grid-template-columns: 1fr;
    grid-template-rows: auto 1fr;
    gap: 1rem;
    padding: 1rem;
  }

  .fullscreen-video-container {
    height: 300px; /* 고정 높이: YouTube 컨트롤 포함 */
    overflow: hidden;
  }

  .fullscreen-controls {
    gap: 1rem;
  }

  .fullscreen-control-btn {
    width: 60px;
    height: 60px;
    font-size: 1.4rem;
    padding: 1rem;
  }

  .fullscreen-song-title {
    font-size: 1.4rem;
  }

  .fullscreen-song-artist {
    font-size: 1rem;
  }
}

@media (max-width: 480px) {
  .player-panel {
    padding: 1rem;
    border-radius: 15px;
    max-width: 100%;
    overflow-x: hidden;
    box-sizing: border-box;
  }

  .youtube-player {
    height: 180px;
  }

  .video-container.fullscreen {
    height: 400px; /* 모바일: YouTube 컨트롤 포함 고정 높이 */
    flex-shrink: 0; /* 자막이 늘어나도 크기 유지 */
  }

  .subtitle-area.fullscreen {
    padding: 0.75rem;
  }

  .current-song-title {
    font-size: 1rem;
  }

  .control-btn {
    padding: 0.6rem;
    font-size: 1rem;
    min-width: 40px;
    height: 40px;
  }



  /* 저장된 구간 목록 모바일 최적화 */
  .saved-section-lyrics {
    font-size: 0.85rem;
    max-height: 100px;
    padding: 0.6rem;
  }

  .saved-section-pronunciation {
    font-size: 0.7rem;
    max-height: 50px;
    padding: 0.3rem 0.5rem;
  }

  .song-item {
    padding: 0.6rem;
  }

  .song-title {
    font-size: 0.85rem;
  }

  .song-artist {
    font-size: 0.75rem;
  }
}

/* 전체화면 플레이어 추가 스타일 */
.fullscreen-title {
  font-size: 1.5rem;
  font-weight: bold;
  color: white;
}

.fullscreen-player .close-btn {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  padding: 0.8rem;
  border-radius: 50%;
  cursor: pointer;
  font-size: 1.2rem;
  transition: all 0.3s ease;
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.fullscreen-player .close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.1);
}

/* 전체화면에서는 더 큰 영상 사이즈 사용 */
.fullscreen-player .video-placeholder {
  height: 400px;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 0;
  font-size: 1.2rem;
  flex: 1;
}

.fullscreen-player .video-container {
  border-radius: 10px;
  overflow: hidden;
  height: 800px; /* 데스크톱: YouTube 전체 UI 표시 (동영상 더보기 포함) */
  margin-bottom: 0;
  flex: none;
  display: flex;
}


.fullscreen-playlist-area .songs-list {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  /* 스크롤바 숨기기 - 웹킷 브라우저 */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* Internet Explorer 10+ */
}

.fullscreen-playlist-area .songs-list::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Opera */
}

/* 전체화면 모바일 대응 */
@media (max-width: 768px) {
  .fullscreen-main {
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.5rem;
    height: auto;
    min-height: calc(100vh - 80px);
    overflow-y: auto;
    overflow-x: hidden;
  }

  .fullscreen-main .fullscreen-player-area {
    max-width: 100% !important;
    flex: none !important;
    overflow: visible !important;
    display: flex !important;
    flex-direction: column !important;
    gap: 0.5rem !important;
    height: auto !important;
    max-height: none !important;
    min-height: auto !important;
  }

  .fullscreen-playlist-area {
    max-width: 100%;
    flex: none;
    min-height: 250px;
    overflow: hidden;
  }

  /* 전체화면 모바일에서 영상 영역을 적절한 크기로 설정 */
  .fullscreen-main .fullscreen-player-area .video-container,
  .fullscreen-main .fullscreen-player-area .video-placeholder {
    height: 400px !important; /* 고정 높이: YouTube 컨트롤 포함 */
    overflow: hidden !important;
    flex: none !important;
    margin-bottom: 0.5rem;
  }

  .fullscreen-main .fullscreen-player-area .youtube-player {
    height: 100% !important;
    min-height: 300px !important;
  }

  /* 컨트롤 영역 최소화 */
  .fullscreen-main .fullscreen-player-area .controls,
  .fullscreen-main .fullscreen-player-area .progress-bar {
    flex: none !important;
    margin-bottom: 0.5rem;
  }


  .fullscreen-main .fullscreen-player-area .saved-sections-container {
    display: none !important; /* 저장된 구간 목록 숨김 */
  }


  .fullscreen-player-area .saved-section-item {
    margin-bottom: 0.8rem;
  }

  .fullscreen-player-area .saved-section-lyrics {
    max-height: 200px;
  }

  .fullscreen-player-area .saved-section-pronunciation {
    max-height: 80px;
  }

  .fullscreen-song-info {
    margin-bottom: 0.1rem;
  }

  .fullscreen-controls {
    margin-bottom: 0.1rem;
  }

  .fullscreen-control-btn {
    width: 45px;
    height: 45px;
    font-size: 1rem;
    padding: 0.5rem;
  }

  .fullscreen-song-title {
    font-size: 0.9rem;
  }

  .fullscreen-song-artist {
    font-size: 0.7rem;
  }
}

/* 모달 스타일 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10003;
  backdrop-filter: blur(4px);
}

.modal-container {
  background: white;
  border-radius: 16px;
  width: 90%;
  max-width: 800px;
  max-height: 85vh;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: modalSlideUp 0.3s ease-out;
  display: flex;
  flex-direction: column;
}

@keyframes modalSlideUp {
  from {
    opacity: 0;
    transform: translateY(30px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
}

.modal-close-btn {
  background: none;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;
}

.modal-close-btn:hover {
  background-color: rgba(255, 255, 255, 0.2);
}

.modal-body {
  padding: 1.5rem;
  flex: 1;
  overflow-y: auto;
}


.form-group {
  margin-bottom: 1.25rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #374151;
}

.required {
  color: #ef4444;
}

.form-group input,
.form-group textarea {
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 1rem;
  font-family: inherit;
  transition: border-color 0.2s ease;
  resize: vertical;
  box-sizing: border-box;
}

.form-group input:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.form-group textarea {
  min-height: 80px;
}

/* AI 가사 추출 UI */
.lyrics-extract-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
  gap: 1rem;
}

.lyrics-extract-header > label {
  margin-bottom: 0 !important;
}

.ai-extract-controls {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.language-selector-inline {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.language-selector-inline label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  margin: 0;
  white-space: nowrap;
}

.language-selector-inline select {
  padding: 0.5rem 0.75rem;
  border: 2px solid #e5e7eb;
  border-radius: 6px;
  font-size: 0.875rem;
  background-color: white;
  cursor: pointer;
  transition: border-color 0.2s ease;
  min-width: 100px;
}

.language-selector-inline select:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.ai-extract-btn {
  padding: 0.5rem 1rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.ai-extract-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.ai-extract-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.ai-extract-btn.loading {
  pointer-events: none;
}

.ai-extract-btn.secondary {
  background: #6b7280;
  padding: 0.4rem 0.75rem;
}

.ai-extract-btn.secondary:hover {
  background: #4b5563;
  box-shadow: 0 2px 8px rgba(107, 114, 128, 0.3);
}

.spinner-small {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.lyrics-extractor-container {
  margin-bottom: 1rem;
  padding: 1rem;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}

.lyrics-input-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.lyrics-input-column {
  display: flex;
  flex-direction: column;
}

.lyrics-input-column label {
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #374151;
  font-size: 0.875rem;
}

.lyrics-input-column textarea {
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 1rem;
  font-family: inherit;
  resize: vertical;
  transition: border-color 0.2s ease;
}

.lyrics-input-column textarea:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.language-selector {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e5e7eb;
}

.language-selector label {
  font-weight: 500;
  color: #374151;
  font-size: 0.875rem;
  margin: 0;
}

.language-selector select {
  padding: 0.5rem 0.75rem;
  border: 2px solid #e5e7eb;
  border-radius: 6px;
  font-size: 0.875rem;
  background-color: white;
  cursor: pointer;
  transition: border-color 0.2s ease;
}

.language-selector select:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1.5rem;
  border-top: 1px solid #e5e7eb;
  background-color: #f9fafb;
}

.modal-btn {
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
}

.modal-btn.secondary {
  background-color: #6b7280;
  color: white;
}

.modal-btn.secondary:hover {
  background-color: #4b5563;
}

.modal-btn.primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.modal-btn.primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.modal-btn.primary:disabled {
  background: #9ca3af;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

/* 반응형 디자인 */
@media (max-width: 640px) {
  .modal-container {
    width: 95%;
    margin: 0 1rem;
  }

  .modal-header,
  .modal-body,
  .modal-footer {
    padding: 1rem;
  }

  .modal-footer {
    flex-direction: column;
  }

  .modal-btn {
    width: 100%;
  }
}

/* 구간 선택 가이드 힌트 */
.guide-hint {
  display: inline-block;
  padding: 0.4rem 0.75rem;
  background: rgba(251, 191, 36, 0.15);
  border: 1px solid rgba(251, 191, 36, 0.3);
  border-radius: 6px;
  color: #fbbf24;
  font-size: 0.8rem;
  margin-left: 0.75rem;
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* 모바일 반응형 */
@media (max-width: 768px) {
  .guide-hint {
    font-size: 0.7rem;
    padding: 0.3rem 0.6rem;
    margin-left: 0.5rem;
  }
}
</style>